import { useState, useEffect, useCallback, useRef, useId } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import GlassCard from "@/components/GlassCard";
import { BlurText, ScrollReveal, SpotlightCard, Magnet, TypewriterText } from "@/components/animations";
import api, { API_URL } from "@/lib/api";
import { toast } from "sonner";
import { FF_DIGILOCKER, FF_IWEBTECHNO_DIRECT } from "@/lib/featureFlags";
import {
    Lock, Mail, User, Github, Shield, GraduationCap, CreditCard,
    Code2, Share2, Layers, FileText, CheckCircle2, ChevronRight,
    ChevronLeft, Upload, Globe, ArrowRight, Brain, Eye, EyeOff, Sparkles,
    AlertCircle, X, Loader2, ExternalLink, Paperclip, Landmark,
    Database, Star, TrendingUp, Award
} from "lucide-react";

const STEPS = [
    { label: "Welcome", icon: Sparkles },
    { label: "Consent", icon: Shield },
    { label: "Academic", icon: GraduationCap },
    { label: "Fees", icon: CreditCard },
    { label: "Profiles", icon: Code2 },
    { label: "Documents", icon: FileText },
    { label: "Ecom Consent", icon: Share2 },
    { label: "Spending", icon: TrendingUp },
    { label: "Review", icon: CheckCircle2 },
];

const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

function StepHeading({ title, subtitle }) {
    return (
        <>
            <BlurText className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-text-primary mb-2">
                {title}
            </BlurText>
            {subtitle ? <p className="text-text-secondary text-sm mb-6">{subtitle}</p> : null}
        </>
    );
}

/* ─── Progress Bar ─── */
function ProgressBar({ steps, activeIndex }) {
    return (
        // Bug 1: outer div scrollable so step 1 is never clipped on narrow mobile screens.
        // -mx-2/px-2 lets it bleed slightly under the card padding for a clean edge.
        <div className="overflow-x-auto -mx-2 px-2 pb-1 mb-8 no-scrollbar" data-testid="progress-bar">
            <div className="flex items-center gap-1 sm:gap-2 min-w-max mx-auto">
                {steps.map((s, i) => (
                    <div key={s.label} className="flex items-center">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                            i < activeIndex ? "bg-blue-primary text-white" :
                            i === activeIndex ? "bg-blue-primary/30 text-blue-primary ring-2 ring-blue-primary/50" :
                            "bg-white/5 text-text-secondary/40"
                        }`}>
                            {i < activeIndex ? <CheckCircle2 size={14} /> : i + 1}
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`w-4 sm:w-8 h-0.5 mx-0.5 transition-colors ${i < activeIndex ? "bg-blue-primary" : "bg-white/10"}`} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* Bug 8: Strip UUID-style prefixes and apply middle-ellipsis for long filenames on mobile */
function truncateFilename(name, maxLen = 28) {
    if (!name) return "";
    // Strip leading UUID/hash prefix (e.g. "4826b47766edc4b9717f_Birth_Certificate.pdf")
    const cleaned = name.replace(/^[0-9a-f]{8,}_/, "");
    if (cleaned.length <= maxLen) return cleaned;
    const dot = cleaned.lastIndexOf(".");
    const ext = dot !== -1 ? cleaned.slice(dot) : "";
    const base = dot !== -1 ? cleaned.slice(0, dot) : cleaned;
    const keepChars = Math.max(6, maxLen - ext.length - 3);
    return `${base.slice(0, keepChars)}…${ext}`;
}

/* ─── File Drop Zone ─── */
function FileDropZone({ onFile, file, accept = ".pdf,.jpg,.jpeg,.png", label = "Upload file" }) {
    const [drag, setDrag] = useState(false);
    const handleDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); };
    return (
        <SpotlightCard className="p-0" spotlightColor="rgba(99,102,241,0.12)">
            <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                    drag ? "border-blue-primary bg-blue-primary/5" : file ? "border-green-pioneer/30 bg-green-pioneer/5" : "border-white/10 hover:border-white/20"
                }`}
                onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = accept; inp.onchange = (e) => { if (e.target.files[0]) onFile(e.target.files[0]); }; inp.click(); }}
                data-testid="file-drop-zone"
            >
                {file ? (
                    <div className="flex items-center justify-center gap-2 text-green-pioneer text-sm flex-wrap">
                        <CheckCircle2 size={16} className="shrink-0" />
                        <span className="truncate max-w-[180px] sm:max-w-xs" title={file.name}>{truncateFilename(file.name)}</span>
                        <span className="text-text-secondary text-xs shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                        <button onClick={(e) => { e.stopPropagation(); onFile(null); }} className="ml-1 text-text-secondary hover:text-red-starter shrink-0"><X size={14} /></button>
                    </div>
                ) : (
                    <>
                        <Upload size={24} className="mx-auto text-text-secondary/50 mb-2" />
                        <p className="text-text-secondary text-sm">{label}</p>
                        <p className="text-text-secondary/50 text-xs mt-1">PDF, JPG, or PNG — drag & drop or click</p>
                    </>
                )}
            </div>
        </SpotlightCard>
    );
}

/* ─── Toggle Card ─── */
function ToggleCard({ icon: Icon, title, desc, enabled, onChange, disabled, badge }) {
    return (
        <div className={`p-4 rounded-xl border transition-all ${enabled ? "bg-blue-primary/5 border-blue-primary/20" : "bg-white/[0.02] border-white/5"} ${disabled ? "opacity-50" : ""}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${enabled ? "bg-blue-primary/15" : "bg-white/5"}`}>
                        <Icon size={18} className={enabled ? "text-blue-primary" : "text-text-secondary/50"} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <p className="text-text-primary text-sm font-medium">{title}</p>
                            {badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary">{badge}</span>}
                        </div>
                        <p className="text-text-secondary text-xs mt-0.5">{desc}</p>
                    </div>
                </div>
                {/* These toggles are how consent is given, so the control has to
                    say what it is and whether it is on. Without role/aria-checked
                    its state is carried by colour alone, which is not something
                    you can rely on for a consent decision in a credit product. */}
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`${title} — share this data`}
                    onClick={() => !disabled && onChange(!enabled)}
                    disabled={disabled}
                    className={`w-10 h-6 rounded-full transition-colors shrink-0 ${enabled ? "bg-blue-primary" : "bg-white/10"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                    data-testid={`toggle-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${enabled ? "translate-x-4" : "translate-x-0"}`} />
                </button>
            </div>
        </div>
    );
}

/* ─── DigiLocker Coming Soon Modal ─── */
function DigiLockerModal({ open, onClose, onUseUpload, onUseManual }) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-void/80 backdrop-blur-2xl flex items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="digilocker-modal"
                >
                    <motion.div
                        initial={{ scale: 0.92, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.92, y: 20, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-md w-full bg-[#0d1526] border border-white/10 rounded-2xl p-7 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400/25 to-yellow-600/10 border border-amber-400/20 flex items-center justify-center">
                                    <Landmark size={22} className="text-amber-300" />
                                </div>
                                <div>
                                    <h3 className="font-heading text-lg font-semibold text-text-primary">DigiLocker</h3>
                                    <span className="text-[10px] font-semibold tracking-wider uppercase text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full">Coming Soon</span>
                                </div>
                            </div>
                            <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors" data-testid="digilocker-close">
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-text-secondary text-sm leading-relaxed mb-5">
                            We're building a government-verified integration with <span className="text-text-primary font-medium">DigiLocker</span> —
                            the Ministry of Electronics &amp; IT's digital document platform. This will let you share your Aadhaar,
                            official marksheets, and degree certificates with the <span className="text-amber-300 font-medium">highest tier of confidence (0.95)</span>.
                        </p>

                        <div className="bg-amber-400/5 border border-amber-400/15 rounded-xl p-3 mb-5">
                            <p className="text-xs text-amber-200/90 flex items-start gap-2">
                                <Sparkles size={12} className="mt-0.5 shrink-0 text-amber-300" />
                                <span>Once live, you'll see a <span className="font-semibold">🏛️ DigiLocker Verified</span> badge on your score card &mdash; unlocking the highest score ceiling.</span>
                            </p>
                        </div>

                        <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">For now, you can still share:</p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => { onClose(); onUseUpload(); }}
                                className="flex flex-col items-center justify-center gap-1.5 p-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-xl transition-all"
                                data-testid="digilocker-upload-fallback"
                            >
                                <Upload size={16} className="text-blue-primary" />
                                <span className="text-xs text-text-primary font-medium">Upload Document</span>
                                <span className="text-[10px] text-text-secondary">Marksheet, ID proof</span>
                            </button>
                            <button
                                onClick={() => { onClose(); onUseManual(); }}
                                className="flex flex-col items-center justify-center gap-1.5 p-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-xl transition-all"
                                data-testid="digilocker-manual-fallback"
                            >
                                <FileText size={16} className="text-cyan-signal" />
                                <span className="text-xs text-text-primary font-medium">Enter Manually</span>
                                <span className="text-[10px] text-text-secondary">Self-declared</span>
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/* ─── iWebTechno ERP Mock Connector ─── */
const IWEBTECHNO_ACADEMIC_MOCK = {
    institution: "VJTI Mumbai",
    institution_tier: "tier_1",
    course: "Computer Engineering",
    category: "engineering",
    year: 3,
    cgpa: 8.2,
    scale: "10",
    grade_trend: "improving",
    attendance: 84,
    class_rank_percentile: 18,
    semester_history: [
        { sem: 1, cgpa: 7.6 },
        { sem: 2, cgpa: 7.9 },
        { sem: 3, cgpa: 8.0 },
        { sem: 4, cgpa: 8.1 },
        { sem: 5, cgpa: 8.2 },
    ],
    _source: "iwebtechno_erp",
};

const IWEBTECHNO_FEE_MOCK = {
    pattern: "semester",
    timing: "mostly_on_time",
    payer: "parent",
    loan_detected: false,
    loan_bank: null,
    annual_fee: 82000,
    on_time_ratio: "5 of 6 semesters on-time",
    total_semesters_paid: 6,
    on_time_semesters: 5,
    _source: "iwebtechno_erp",
};

/* ─── Ecom Consumption Mock Data ─── */
const ECOM_MOCK_DATA = {
    monthly_spend_avg: 18450,
    spend_trend_6mo: [12200, 14100, 15300, 16400, 17100, 18450],
    spending_breakdown: {
        essentials: { pct: 68, amount: 12546, label: "Essentials (Amazon, groceries)" },
        food_delivery: { pct: 22, amount: 4059, label: "Food Delivery (Zomato, Swiggy)" },
        discretionary: { pct: 10, amount: 1845, label: "Discretionary (lifestyle)" },
    },
    signals: [
        { name: "Amazon Prime", detail: "Active 18 months", points: 42, color: "#3b82f6" },
        { name: "Order Consistency", detail: "4.8/5 (very stable)", points: 35, color: "#10b981" },
        { name: "Quick-Commerce Habit", detail: "12 Zepto orders/month", points: 28, color: "#8b5cf6" },
        { name: "Food Delivery Pattern", detail: "Stable ₹3.2K/month", points: 22, color: "#f59e0b" },
        { name: "Return Rate", detail: "Only 3%", points: 15, color: "#06b6d4" },
    ],
    total_consumption_uplift: 142,
    traditional_cibil: 640,
    full_alternative_score: 782,
    consumption_sub_score: 78,
};

const SCORE_LOADING_MESSAGES = ["Verifying your evidence...", "Analyzing signals...", "Computing your score...", "Generating insights..."];

const PLATFORM_HOSTS = {
    github: ["github.com"],
    linkedin: ["linkedin.com"],
    twitter: ["twitter.com", "x.com"],
    instagram: ["instagram.com"],
    youtube: ["youtube.com", "youtu.be"],
    reddit: ["reddit.com"],
};

function normalizeProfileUrl(value) {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    url.hash = "";
    url.search = "";
    url.hostname = url.hostname.toLowerCase().replace(/^www\./, "");
    url.pathname = url.pathname.replace(/\/+$/, "");
    return url.toString().replace(/\/$/, "");
}

function validateProfileUrl(platform, value) {
    let normalized;
    try {
        normalized = normalizeProfileUrl(value);
        const parsed = new URL(normalized);
        const host = parsed.hostname;
        const allowed = PLATFORM_HOSTS[platform];
        if (allowed && !allowed.some(domain => host === domain || host.endsWith(`.${domain}`))) {
            return { ok: false, error: `Enter a valid ${platform === "twitter" ? "Twitter / X" : platform} profile URL` };
        }

        // Bug 5: GitHub-specific validation — must be a profile URL (one path segment),
        // not a repo URL (two segments) or an org/team page.
        if (platform === "github") {
            const parts = parsed.pathname.split("/").filter(Boolean);
            if (parts.length === 0) {
                return { ok: false, error: "Enter your GitHub profile URL (e.g. github.com/username)" };
            }
            if (parts.length > 1) {
                return { ok: false, error: "Enter your GitHub profile URL (e.g. github.com/username), not a repository link" };
            }
            // GitHub usernames: 1–39 chars, alphanumeric + hyphens, no consecutive hyphens, no leading/trailing hyphen
            const username = parts[0];
            if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/.test(username) && !/^[a-zA-Z0-9]$/.test(username)) {
                return { ok: false, error: "Enter a valid GitHub username (letters, numbers, hyphens only)" };
            }
        }
    } catch {
        return { ok: false, error: "Enter a valid public profile URL" };
    }
    return { ok: true, url: normalized };
}

function MiniTrendChart({ points }) {
    const gradId = useId();
    if (!points || points.length === 0) return null;
    const max = Math.max(...points.map(p => p.cgpa));
    const min = Math.min(...points.map(p => p.cgpa));
    const range = max - min || 1;
    const W = 200, H = 44, PAD = 4;
    const step = (W - PAD * 2) / (points.length - 1 || 1);
    const d = points
        .map((p, i) => {
            const x = PAD + i * step;
            const y = PAD + (H - PAD * 2) * (1 - (p.cgpa - min) / range);
            return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(" ");
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10" preserveAspectRatio="none">
            <defs>
                <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={`${d} L ${W - PAD} ${H} L ${PAD} ${H} Z`} fill={`url(#${gradId})`} />
            <path d={d} fill="none" stroke="#3b82f6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IWebTechnoAcademicCard({ onUse, used }) {
    const [stage, setStage] = useState("idle"); // idle | loading | ready
    const mock = IWEBTECHNO_ACADEMIC_MOCK;

    const handleConnect = () => {
        setStage("loading");
        setTimeout(() => setStage("ready"), 2000);
    };

    return (
        <div className="space-y-3" data-testid="iwebtechno-academic">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-blue-primary/15 border border-blue-primary/20 flex items-center justify-center">
                    <Database size={15} className="text-blue-primary" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-text-primary text-sm font-semibold">iWebTechno ERP</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary flex items-center gap-0.5">
                            <Star size={9} className="fill-blue-primary" /> RECOMMENDED
                        </span>
                        {!FF_IWEBTECHNO_DIRECT && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-text-secondary">DEMO</span>
                        )}
                    </div>
                    <p className="text-text-secondary text-xs mt-0.5">Pull CGPA, attendance, rank directly from your college ERP.</p>
                </div>
            </div>

            {stage === "idle" && !used && (
                <button
                    onClick={handleConnect}
                    className="w-full bg-blue-primary text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all inline-flex items-center justify-center gap-2"
                    data-testid="iwebtechno-academic-connect"
                >
                    <Database size={16} /> Connect iWebTechno ERP
                </button>
            )}

            {stage === "loading" && (
                <div className="p-5 bg-blue-primary/5 border border-blue-primary/15 rounded-xl" data-testid="iwebtechno-academic-loading">
                    <div className="flex items-center gap-3 mb-3">
                        <Loader2 size={18} className="animate-spin text-blue-primary" />
                        <p className="text-text-primary text-sm font-medium">Connecting to college ERP...</p>
                    </div>
                    <div className="space-y-2">
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div initial={{ width: "10%" }} animate={{ width: "100%" }} transition={{ duration: 1.9, ease: "easeOut" }} className="h-full bg-gradient-to-r from-blue-primary to-cyan-signal" />
                        </div>
                        <p className="text-text-secondary text-[11px]">Verifying credentials · Fetching transcript · Validating records</p>
                    </div>
                </div>
            )}

            {(stage === "ready" || used) && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-blue-primary/5 border border-blue-primary/25 rounded-xl space-y-3"
                    data-testid="iwebtechno-academic-ready"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={14} className="text-green-pioneer" />
                            <span className="text-text-primary text-sm font-semibold">{mock.institution}</span>
                        </div>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary">Verified via ERP</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        <div className="p-2.5 bg-white/[0.03] rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-0.5">CGPA</p>
                            <p className="text-text-primary text-base font-mono font-semibold flex items-center gap-1">
                                {mock.cgpa}
                                <TrendingUp size={11} className="text-green-pioneer" />
                            </p>
                        </div>
                        <div className="p-2.5 bg-white/[0.03] rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-0.5">Class Rank</p>
                            <p className="text-text-primary text-base font-mono font-semibold">Top {mock.class_rank_percentile}%</p>
                        </div>
                        <div className="p-2.5 bg-white/[0.03] rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-0.5">Attendance</p>
                            <p className="text-text-primary text-base font-mono font-semibold">{mock.attendance}%</p>
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-1">CGPA Trend (5 sems)</p>
                        <MiniTrendChart points={mock.semester_history} />
                    </div>

                    <p className="text-[11px] text-text-secondary">
                        {mock.course} · Year {mock.year} · Improving trend
                    </p>

                    {!used && (
                        <button
                            onClick={() => onUse(mock)}
                            className="w-full bg-blue-primary text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all inline-flex items-center justify-center gap-2"
                            data-testid="iwebtechno-academic-use"
                        >
                            <CheckCircle2 size={14} /> Use This Data
                        </button>
                    )}
                    {used && (
                        <div className="w-full bg-green-pioneer/10 border border-green-pioneer/25 text-green-pioneer px-4 py-2 rounded-xl text-xs font-medium inline-flex items-center justify-center gap-1.5">
                            <CheckCircle2 size={13} /> Saved as verified evidence
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
}

function IWebTechnoFeeCard({ onUse, used }) {
    const [stage, setStage] = useState("idle");
    const mock = IWEBTECHNO_FEE_MOCK;

    const handleConnect = () => {
        setStage("loading");
        setTimeout(() => setStage("ready"), 2000);
    };

    return (
        <div className="space-y-3" data-testid="iwebtechno-fee">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-blue-primary/15 border border-blue-primary/20 flex items-center justify-center">
                    <Database size={15} className="text-blue-primary" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-text-primary text-sm font-semibold">iWebTechno ERP</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary flex items-center gap-0.5">
                            <Star size={9} className="fill-blue-primary" /> RECOMMENDED
                        </span>
                        {!FF_IWEBTECHNO_DIRECT && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-text-secondary">DEMO</span>
                        )}
                    </div>
                    <p className="text-text-secondary text-xs mt-0.5">Pull your semester-wise fee payment history directly from the college.</p>
                </div>
            </div>

            {stage === "idle" && !used && (
                <button
                    onClick={handleConnect}
                    className="w-full bg-blue-primary text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all inline-flex items-center justify-center gap-2"
                    data-testid="iwebtechno-fee-connect"
                >
                    <Database size={16} /> Connect iWebTechno ERP
                </button>
            )}

            {stage === "loading" && (
                <div className="p-5 bg-blue-primary/5 border border-blue-primary/15 rounded-xl" data-testid="iwebtechno-fee-loading">
                    <div className="flex items-center gap-3 mb-3">
                        <Loader2 size={18} className="animate-spin text-blue-primary" />
                        <p className="text-text-primary text-sm font-medium">Fetching fee ledger...</p>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div initial={{ width: "10%" }} animate={{ width: "100%" }} transition={{ duration: 1.9, ease: "easeOut" }} className="h-full bg-gradient-to-r from-blue-primary to-cyan-signal" />
                    </div>
                </div>
            )}

            {(stage === "ready" || used) && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-blue-primary/5 border border-blue-primary/25 rounded-xl space-y-3"
                    data-testid="iwebtechno-fee-ready"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={14} className="text-green-pioneer" />
                            <span className="text-text-primary text-sm font-semibold">Fee Ledger</span>
                        </div>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary">Verified via ERP</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="p-2.5 bg-white/[0.03] rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-0.5">On-Time</p>
                            <p className="text-text-primary text-base font-mono font-semibold">{mock.on_time_semesters} / {mock.total_semesters_paid}</p>
                        </div>
                        <div className="p-2.5 bg-white/[0.03] rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-text-secondary/60 mb-0.5">Annual Fee</p>
                            <p className="text-text-primary text-base font-mono font-semibold">₹{(mock.annual_fee / 1000).toFixed(0)}K</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary capitalize">{mock.pattern}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-pioneer/15 text-green-pioneer capitalize">{mock.timing.replace(/_/g, " ")}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary capitalize">{mock.payer}-funded</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary">No loan</span>
                    </div>

                    {!used && (
                        <button
                            onClick={() => onUse(mock)}
                            className="w-full bg-blue-primary text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all inline-flex items-center justify-center gap-2"
                            data-testid="iwebtechno-fee-use"
                        >
                            <CheckCircle2 size={14} /> Use This Data
                        </button>
                    )}
                    {used && (
                        <div className="w-full bg-green-pioneer/10 border border-green-pioneer/25 text-green-pioneer px-4 py-2 rounded-xl text-xs font-medium inline-flex items-center justify-center gap-1.5">
                            <CheckCircle2 size={13} /> Saved as verified evidence
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
}

/* ─── College Autocomplete ─── */
function CollegeAutocomplete({ value, onChange }) {
    const [query, setQuery] = useState(value || "");
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        if (query.length < 2) { setSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const res = await api.get(`/api/nirf/search?q=${encodeURIComponent(query)}`);
                setSuggestions(res.data.results || []);
                setShowDropdown(true);
            } catch { setSuggestions([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={wrapRef} className="relative">
            <input type="text" placeholder="College name (e.g. IIT Bombay)" value={query}
                onChange={e => { setQuery(e.target.value); onChange(e.target.value); }}
                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                className="glass-input text-sm" data-testid="acad-college" />
            {showDropdown && suggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0d1526] border border-white/10 rounded-xl overflow-hidden shadow-2xl max-h-48 overflow-y-auto" data-testid="college-suggestions">
                    {suggestions.map((s) => (
                        <button key={s.name} type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors flex items-center justify-between"
                            onClick={() => { setQuery(s.name); onChange(s.name); setShowDropdown(false); }}>
                            <span className="text-text-primary text-sm">{s.name}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                                s.tier === "tier_1" ? "bg-green-pioneer/15 text-green-pioneer" :
                                s.tier === "tier_2" ? "bg-amber-builder/15 text-amber-builder" :
                                "bg-white/10 text-text-secondary"
                            }`}>{s.tier?.replace("_", " ")}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Platform Connector with TinyFish Fetch ─── */
// Bug 3: accept initialFetchedData so data survives back→continue remounts
function PlatformCard({ name, icon: Icon, color, onSave, savedStatus, onFetchStart, onFetchEnd, initialFetchedData }) {
    const [url, setUrl] = useState("");
    const [manual, setManual] = useState({});
    const [showManual, setShowManual] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [fetchedData, setFetchedData] = useState(initialFetchedData || null);
    const [fetchPhaseMsg, setFetchPhaseMsg] = useState(""); // live progress message

    const manualFields = {
        github: [["repos", "Repositories", "number"], ["stars", "Stars", "number"], ["languages", "Languages (comma separated)", "text"]],
        linkedin: [["title", "Job Title", "text"], ["company", "Company", "text"], ["years", "Years Experience", "number"], ["skills", "Skills (comma separated)", "text"]],
        youtube: [["subscribers", "Subscribers", "number"], ["videos", "Video Count", "number"]],
        twitter: [["followers", "Followers", "number"], ["tweets", "Tweets", "number"], ["age", "Account Age (years)", "number"]],
        instagram: [["followers", "Followers", "number"], ["posts", "Posts", "number"]],
        reddit: [["karma", "Karma", "number"], ["age", "Account Age (years)", "number"]],
        portfolio: [["skills", "Skills (comma separated)", "text"], ["projects", "Project Descriptions", "text"]],
    };

    const handleFetchProfile = async () => {
        const validated = validateProfileUrl(name, url);
        if (!validated.ok) {
            setFetchError(validated.error);
            setShowManual(true);
            toast.error(validated.error);
            return;
        }
        const normalizedUrl = validated.url;
        setFetching(true);
        setFetchError("");
        setFetchedData(null);
        setFetchPhaseMsg("Starting profile analysis...");
        if (onFetchStart) onFetchStart();

        // ── Progress messages by elapsed seconds ───────────────────────
        // LinkedIn, Instagram, Twitter use browser automation (TinyFish) and
        // typically take 45–120s on a cold scrape. We show phase-based messages
        // so users know to wait instead of clicking again.
        const SLOW_PLATFORMS = ["linkedin", "instagram", "twitter", "youtube"];
        const isSlowPlatform = SLOW_PLATFORMS.includes(name);
        const phaseMessages = [
            { at: 0,  msg: `Connecting to ${name}...` },
            { at: 8,  msg: `Analyzing your ${name} profile...` },
            { at: 25, msg: isSlowPlatform ? `${name} uses browser automation — usually takes 45–90s...` : `Extracting your data...` },
            { at: 55, msg: `Almost done — finalizing your profile data...` },
            { at: 80, msg: `Taking a bit longer than usual — hang tight...` },
        ];

        const finish = (ok, data, err) => {
            setFetchPhaseMsg("");
            if (ok && data) {
                // Bug 5 (post-fetch guard): for GitHub, verify the returned username
                // matches the URL slug so a wrong URL doesn't silently show someone
                // else's data. Apify can sometimes return cached/nearby profiles.
                if (name === "github" && data.username) {
                    const urlSlug = url.trim().replace(/\/$/, "").split("/").filter(Boolean).pop();
                    if (urlSlug && data.username.toLowerCase() !== urlSlug.toLowerCase()) {
                        const displayErr = `GitHub returned data for "${data.username}" but you entered "${urlSlug}". Please check the URL and try again.`;
                        setFetchError(displayErr);
                        setShowManual(true);
                        if (onFetchEnd) onFetchEnd();
                        return;
                    }
                }
                setFetchedData(data);
                // Backend already persisted evidence — don't overwrite with a
                // lower-quality frontend re-save. Just update wizard UI state.
                onSave({ type: "url_ingest", data, confidence: 0.55 });
                toast.success(`${name} profile data extracted!`);
            } else {
                let displayErr = err || "Could not extract profile data";
                // Remap auth/private/blocked errors to a friendlier message
                const errLower = displayErr.toLowerCase();
                if (errLower.includes("authentic") || errLower.includes("auth") || errLower.includes("private") || errLower.includes("403") || errLower.includes("login")) {
                    displayErr = "This profile may be private or blocked. Enter your details manually below.";
                }
                setFetchError(displayErr);
                setShowManual(true);
                if (displayErr && !displayErr.includes("manually")) toast.error(displayErr);
            }
            setFetching(false);
            if (onFetchEnd) onFetchEnd();
        };

        // ── Phase-message ticker ────────────────────────────────────────
        const phaseStart = Date.now();
        let phaseTimer;
        const tickPhaseMessage = () => {
            const elapsed = (Date.now() - phaseStart) / 1000;
            const active = phaseMessages.filter(p => elapsed >= p.at);
            if (active.length) setFetchPhaseMsg(active[active.length - 1].msg);
            phaseTimer = setTimeout(tickPhaseMessage, 3000);
        };
        phaseTimer = setTimeout(tickPhaseMessage, 1000);
        const stopPhaseTicker = () => clearTimeout(phaseTimer);

        // ── Core poll loop ──────────────────────────────────────────────
        // Phase 1: 90s. If it times out, Phase 2 auto-retries via the cache
        // endpoint (backend typically finishes by 90s and caches the result).
        // Phase 2: 45s. By now the backend has definitely cached it or truly failed.
        // Only after BOTH phases fail do we show "enter manually".
        const PHASE1_MS = 90000;
        const PHASE2_MS = 45000;

        const buildPollLoop = (jobId, phaseLimitMs, onTimeout) => {
            const start = Date.now();
            let transient = 0;
            const loop = async () => {
                if (Date.now() - start > phaseLimitMs) {
                    onTimeout();
                    return;
                }
                try {
                    const { data: s } = await api.get(`/api/scrape/status/${jobId}`);
                    if (s.status === "completed") {
                        stopPhaseTicker();
                        finish(true, s.data);
                    } else if (s.status === "failed") {
                        stopPhaseTicker();
                        finish(false, null, s.error || "Profile scrape failed. Please enter details manually.");
                    } else {
                        // Still processing — keep polling
                        setTimeout(loop, 2500);
                    }
                } catch (err) {
                    const code = err.response?.status;
                    // 404 = job not on this instance (multi-instance race). Retry.
                    const isTransient = !code || code === 404 || code >= 502;
                    if (isTransient && transient < 6) {
                        transient++;
                        setTimeout(loop, Math.min(6000, 1500 * transient));
                    } else {
                        stopPhaseTicker();
                        finish(false, null, "Network error — please try again.");
                    }
                }
            };
            return loop;
        };

        try {
            // POST to async endpoint — returns cached result immediately OR starts job
            let jobRes;
            try {
                jobRes = await api.post("/api/scrape/async", { url: normalizedUrl, platform: name });
            } catch (asyncErr) {
                const status = asyncErr.response?.status;
                if (status === 400 || status === 422) {
                    stopPhaseTicker();
                    finish(false, null, asyncErr.response?.data?.detail || "Invalid URL for this platform");
                    return;
                }
                if (status === 429) {
                    stopPhaseTicker();
                    finish(false, null, "Too many requests — please wait a moment and retry");
                    return;
                }
                // Async endpoint unavailable → fall back to sync (blocks until TinyFish responds)
                setFetchPhaseMsg("Using direct fetch — this may take up to 90 seconds...");
                try {
                    const res = await api.post("/api/scrape/profile", { url: normalizedUrl, platform: name }, { timeout: 120000 });
                    stopPhaseTicker();
                    finish(res.data.success, res.data.data, res.data.error);
                } catch (syncErr) {
                    stopPhaseTicker();
                    finish(false, null, syncErr.response?.data?.detail || "Could not reach scraping service");
                }
                return;
            }

            // ── Cache hit: return immediately ────────────────────────────
            if (jobRes.data.status === "completed" && jobRes.data.data) {
                stopPhaseTicker();
                finish(true, jobRes.data.data);
                return;
            }
            if (jobRes.data.status === "failed") {
                stopPhaseTicker();
                finish(false, null, jobRes.data.error || "Profile data could not be extracted");
                return;
            }

            const jobId = jobRes.data.job_id;
            if (!jobId) {
                stopPhaseTicker();
                finish(false, null, "Could not start profile fetch. Try again.");
                return;
            }

            // ── Phase 1: poll for up to 90s ─────────────────────────────
            const phase1 = buildPollLoop(jobId, PHASE1_MS, async () => {
                // Phase 1 timed out. Backend is likely still running OR just finished.
                // Auto-retry: issue a fresh async request — if the backend cached
                // the result, this returns instantly. User never sees an error.
                setFetchPhaseMsg("Checking for completed result...");
                try {
                    const retryRes = await api.post("/api/scrape/async", { url: normalizedUrl, platform: name });
                    if (retryRes.data.status === "completed" && retryRes.data.data) {
                        stopPhaseTicker();
                        finish(true, retryRes.data.data);
                        return;
                    }
                    // Got a new job_id → backend still running. Phase 2 poll.
                    const newJobId = retryRes.data.job_id || jobId;
                    setFetchPhaseMsg("Profile analysis in final stage...");
                    const phase2 = buildPollLoop(newJobId, PHASE2_MS, () => {
                        // Both phases exhausted. Truly failed or taking >135s.
                        stopPhaseTicker();
                        finish(false, null,
                            `${name} profile could not be retrieved. ` +
                            "Try again in a few seconds — or enter details manually below."
                        );
                    });
                    setTimeout(phase2, 1000);
                } catch {
                    stopPhaseTicker();
                    finish(false, null, `${name} profile could not be retrieved. Please try again or enter manually.`);
                }
            });
            setTimeout(phase1, 1500);
        } catch (err) {
            stopPhaseTicker();
            finish(false, null, err.response?.data?.detail || "Fetch failed — please try again.");
        }
    };

    const handleSaveManual = () => {
        const hasData = Object.values(manual).some(v => v !== "" && v !== undefined);
        if (!hasData) return;
        onSave({ type: "user_declared", data: manual, confidence: 0.30 });
    };

    const statusBadge = savedStatus === "oauth" ? { label: "Connected", cls: "bg-green-pioneer/15 text-green-pioneer" } :
        savedStatus === "url" ? { label: "URL Extracted", cls: "bg-blue-primary/15 text-blue-primary" } :
        savedStatus === "url_ingest" ? { label: "URL Extracted", cls: "bg-blue-primary/15 text-blue-primary" } :
        savedStatus === "manual" ? { label: "Self-declared", cls: "bg-white/10 text-text-secondary" } : null;

    const fetchBtnLabel = {
        linkedin: "Fetch Profile", github: "Fetch Profile", twitter: "Fetch Profile",
        instagram: "Fetch Profile", reddit: "Fetch Profile", youtube: "Fetch Channel",
        portfolio: "Fetch Website",
    };

    /* Summarize fetched data for display */
    const renderFetchedSummary = () => {
        if (!fetchedData) return null;
        const d = fetchedData;
        const items = [];

        // Safe formatter — handles integers, "14.5K", "1.2M", "71+" (LinkedIn rounds with +)
        const fmt = (v) => {
            if (v == null) return null;
            if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? Math.round(v).toLocaleString("en-IN") : null;
            const s = String(v).trim().replace(/,/g, '').replace(/\+$/, ''); // strip trailing "+"
            const upper = s.toUpperCase();
            let n;
            if (upper.endsWith('K')) n = parseFloat(upper) * 1_000;
            else if (upper.endsWith('M')) n = parseFloat(upper) * 1_000_000;
            else if (upper.endsWith('B')) n = parseFloat(upper) * 1_000_000_000;
            else n = parseFloat(s);
            return isNaN(n) || n < 0 ? null : Math.round(n).toLocaleString("en-IN");
        };

        // Filter out placeholder/unknown strings from the scraper
        const isPlaceholder = (v) => !v || ['@unknown', 'unknown', 'null', 'none', ''].includes(
            String(v).trim().toLowerCase().replace(/^@/, '')
        );
        const displayName = [d.name, d.channel_name, d.display_name, d.username].find(v => v && !isPlaceholder(v));
        if (displayName) items.push(displayName);
        if (d.headline) items.push(d.headline);
        if (d.current_company) items.push(d.current_company);

        // Followers — normalise across all scraper response shapes
        const followersRaw = d.followers_count ?? d.followers ?? d.followersCount ?? d.followersNumber;
        const followersStr = fmt(followersRaw);
        if (followersStr) items.push(`${followersStr} followers`);

        // Subscribers (YouTube)
        const subsRaw = d.subscribers_count ?? d.subscribers ?? d.subscriberCount ?? d.numberOfSubscribers;
        const subsStr = fmt(subsRaw);
        if (subsStr) items.push(`${subsStr} subscribers`);

        // Reddit karma
        const karmaRaw = d.total_karma ?? d.karma ?? d.comment_karma;
        const karmaStr = fmt(karmaRaw);
        if (karmaStr) items.push(`${karmaStr} karma`);

        // GitHub
        const reposRaw = d.public_repos ?? d.repos;
        if (reposRaw != null && fmt(reposRaw)) items.push(`${fmt(reposRaw)} repos`);

        // Videos
        const vidsRaw = d.video_count ?? d.videos ?? d.videoCount ?? d.numberOfVideos;
        if (vidsRaw != null && fmt(vidsRaw)) items.push(`${fmt(vidsRaw)} videos`);

        // Instagram posts
        const postsRaw = d.posts_count ?? d.posts ?? d.postsCount;
        if (postsRaw != null && fmt(postsRaw)) items.push(`${fmt(postsRaw)} posts`);

        // LinkedIn connections
        const connRaw = d.connections_count ?? d.connections ?? d.connectionsCount;
        if (connRaw != null && fmt(connRaw)) items.push(`${fmt(connRaw)}+ connections`);

        // Tweets
        const tweetsRaw = d.tweet_count ?? d.tweets ?? d.tweetsCount ?? d.statuses_count;
        if (tweetsRaw != null && fmt(tweetsRaw)) items.push(`${fmt(tweetsRaw)} tweets`);

        if (Array.isArray(d.skills) && d.skills.length) items.push(`${d.skills.length} skills`);
        if (Array.isArray(d.projects) && d.projects.length) items.push(`${d.projects.length} projects`);
        const langs = d.top_languages ?? d.languages ?? d.topLanguages;
        if (Array.isArray(langs) && langs.length) items.push(langs.slice(0, 3).join(", "));

        return (
            <div className="mt-3 p-3 bg-blue-primary/5 border border-blue-primary/15 rounded-lg" data-testid={`${name}-fetched-summary`}>
                <div className="flex items-center gap-2 mb-2">
                    <Paperclip size={13} className="text-blue-primary" />
                    <span className="text-xs font-semibold text-blue-primary">Extracted Data</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {items.slice(0, 6).map((item, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary">{item}</span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <SpotlightCard className="p-4 min-w-0 overflow-hidden" spotlightColor="rgba(99,102,241,0.12)" data-testid={`platform-${name}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon size={18} style={{ color }} />
                    <span className="text-text-primary text-sm font-medium capitalize">{name === "twitter" ? "Twitter / X" : name}</span>
                </div>
                {statusBadge && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${statusBadge.cls}`}>
                        {(savedStatus === "url" || savedStatus === "url_ingest") && <Paperclip size={10} />}
                        {statusBadge.label}
                    </span>
                )}
            </div>

            {fetchedData && renderFetchedSummary()}

            {!savedStatus && (
                <>
                    {name === "github" && (
                        // Bug 4: store current path BEFORE leaving for GitHub OAuth so
                        // we return the user to the onboarding wizard instead of home.
                        <button
                            type="button"
                            onClick={() => {
                                sessionStorage.setItem("oauth_return_to", window.location.pathname + window.location.search);
                                window.location.href = `${API_URL}/api/oauth/github/start`;
                            }}
                            className="w-full flex items-center justify-center gap-2 bg-elevated text-text-primary border border-white/10 px-4 py-2 rounded-lg hover:bg-white/5 transition-all text-sm font-medium mb-3"
                            data-testid="github-connect-btn">
                            <Github size={16} /> Connect with GitHub
                        </button>
                    )}

                    {/* URL input + Fetch button */}
                    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mb-2 min-w-0">
                        <input type="url" value={url}
                            onChange={(e) => { setUrl(e.target.value); if (fetchError) setFetchError(""); }}
                            placeholder={{
                                github: "github.com/username",
                                linkedin: "linkedin.com/in/username",
                                twitter: "x.com/username",
                                instagram: "instagram.com/username",
                                youtube: "youtube.com/@channel",
                                portfolio: "yourwebsite.com",
                            }[name] || `${name}.com/username`}
                            className="glass-input text-sm flex-1 min-w-0" data-testid={`${name}-url-input`}
                            onKeyDown={(e) => e.key === "Enter" && handleFetchProfile()} />
                        <button onClick={handleFetchProfile} disabled={!url.trim() || fetching}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all inline-flex items-center justify-center gap-1.5 whitespace-nowrap shrink-0 ${
                                fetching
                                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/20 cursor-wait"
                                    : "bg-blue-primary/20 text-blue-primary hover:bg-blue-primary/30 disabled:opacity-30"
                            }`}
                            data-testid={`${name}-fetch-btn`}>
                            {fetching ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                            {fetching ? "Analyzing..." : (fetchBtnLabel[name] || "Fetch")}
                        </button>
                    </div>

                    {/* Live phase progress message shown while fetching */}
                    {fetching && fetchPhaseMsg && (
                        <div className="flex items-center gap-2 bg-blue-primary/5 border border-blue-primary/15 rounded-lg px-3 py-2 mb-2" data-testid={`${name}-fetch-progress`}>
                            <Loader2 size={11} className="text-blue-primary animate-spin shrink-0" />
                            <p className="text-[11px] text-blue-primary/90 leading-snug">{fetchPhaseMsg}</p>
                        </div>
                    )}

                    {/* Error with Retry button — not just "enter manually" */}
                    {fetchError && !fetching && (
                        <div className="rounded-lg border border-red-starter/20 bg-red-starter/5 px-3 py-2 mb-2" data-testid={`${name}-fetch-error`}>
                            <div className="flex items-start gap-2">
                                <AlertCircle size={12} className="text-red-starter shrink-0 mt-0.5" />
                                <p className="text-[11px] text-red-starter leading-snug flex-1">{fetchError}</p>
                            </div>
                            {url.trim() && (
                                <button
                                    onClick={() => { setFetchError(""); handleFetchProfile(); }}
                                    className="mt-2 text-[11px] text-blue-primary hover:underline flex items-center gap-1"
                                    data-testid={`${name}-retry-btn`}
                                >
                                    <Loader2 size={10} /> Try again
                                </button>
                            )}
                        </div>
                    )}

                    {/* Manual entry toggle */}
                    <button onClick={() => setShowManual(!showManual)} className="text-xs text-text-secondary/60 hover:text-text-secondary transition-colors"
                        data-testid={`${name}-manual-toggle`}>
                        {showManual ? "Hide manual entry" : "Or enter details manually"}
                    </button>
                    {showManual && (
                        <div className="mt-2 space-y-2">
                            {(manualFields[name] || []).map(([key, label, type]) => (
                                <input key={key} type={type} placeholder={label}
                                    value={manual[key] || ""} onChange={(e) => setManual(p => ({ ...p, [key]: type === "number" ? Number(e.target.value) || "" : e.target.value }))}
                                    className="glass-input text-sm" data-testid={`${name}-manual-${key}`} />
                            ))}
                            <button onClick={handleSaveManual} className="bg-white/5 text-text-primary px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-white/10 transition-all"
                                data-testid={`${name}-manual-save`}>
                                Save Manual Entry
                            </button>
                        </div>
                    )}
                </>
            )}
        </SpotlightCard>
    );
}

/* ─── Loading Overlay ─── */
function ScoreLoadingOverlay({ onComplete, error, onRetry, onGoBack }) {
    const [step, setStep] = useState(0);
    useEffect(() => {
        if (error) return;
        const timers = SCORE_LOADING_MESSAGES.map((_, i) => setTimeout(() => setStep(i + 1), (i + 1) * 900));
        return () => timers.forEach(clearTimeout);
    }, [error]);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-void/95 backdrop-blur-2xl flex items-center justify-center" data-testid="score-loading">
            <div className="text-center max-w-sm mx-auto px-6">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-primary/20 flex items-center justify-center mb-6">
                    <Brain size={26} className={error ? "text-red-starter" : "text-blue-primary animate-pulse"} />
                </div>
                {error ? (
                    <>
                        <h2 className="font-heading text-xl font-semibold text-red-starter mb-2" data-testid="score-error-title">Score Generation Failed</h2>
                        <p className="text-text-secondary text-sm mb-6 break-words" data-testid="score-error-message">{error}</p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                            <button onClick={onRetry} className="bg-blue-primary text-white px-6 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all" data-testid="score-error-retry">
                                Try Again
                            </button>
                            <button onClick={onGoBack} className="bg-white/5 text-text-primary px-6 py-2.5 rounded-xl font-medium text-sm hover:bg-white/10 transition-all border border-white/10" data-testid="score-error-back">
                                Go Back &amp; Add More Data
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2 className="font-heading text-xl font-semibold text-text-primary mb-6">Calculating your score</h2>
                        <p className="text-cyan-signal text-xs mb-5">
                            <TypewriterText text={SCORE_LOADING_MESSAGES[Math.max(0, Math.min(step - 1, SCORE_LOADING_MESSAGES.length - 1))] || SCORE_LOADING_MESSAGES[0]} speed={22} />
                        </p>
                        <div className="space-y-3">
                            {SCORE_LOADING_MESSAGES.map((m, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${step > i ? "bg-green-pioneer/20" : "bg-white/5"}`}>
                                        {step > i ? <CheckCircle2 size={12} className="text-green-pioneer" /> : <div className="w-2 h-2 rounded-full bg-white/20" />}
                                    </div>
                                    <span className={`text-sm ${step > i ? "text-text-primary" : "text-text-secondary/40"}`}>{m}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
}

/* ═══════════════ Ecom Consumption Dashboard Component ═══════════════ */
function AnimatedCounter({ target, duration = 1800, prefix = "", suffix = "", className = "" }) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        let raf;
        const t0 = performance.now();
        const tick = (now) => {
            const p = Math.min((now - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.floor(eased * target));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return <span className={className}>{prefix}{val.toLocaleString("en-IN")}{suffix}</span>;
}

function Sparkline({ points, width = 320, height = 56, color = "#3b82f6" }) {
    const gradId = useId();
    if (!points || points.length < 2) return null;
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max - min || 1;
    const PAD = 4;
    const step = (width - PAD * 2) / (points.length - 1);
    const pts = points.map((v, i) => [PAD + i * step, PAD + (height - PAD * 2) * (1 - (v - min) / range)]);
    const d = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const last = pts[pts.length - 1];
    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-14">
            <defs>
                <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                d={`${d} L ${last[0]} ${height} L ${PAD} ${height} Z`}
                fill={`url(#${gradId})`}
            />
            <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            />
            <motion.circle
                cx={last[0]} cy={last[1]} r={3.5}
                fill={color}
                initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.5, type: "spring", stiffness: 300 }}
            />
        </svg>
    );
}

function ConicPie({ data, size = 160 }) {
    // data = [{ pct, color, label }]
    let acc = 0;
    const stops = data.map(d => {
        const from = acc; acc += d.pct; return `${d.color} ${from}% ${acc}%`;
    }).join(", ");
    const [hovered, setHovered] = useState(null);
    return (
        <div className="relative inline-block" style={{ width: size, height: size }}>
            <motion.div
                initial={{ scale: 0.7, opacity: 0, rotate: -90 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-full"
                style={{ width: size, height: size, background: `conic-gradient(${stops})` }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[60%] h-[60%] rounded-full bg-[#0d1526] border border-white/10 flex flex-col items-center justify-center">
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary/60">Total</p>
                    <p className="text-text-primary text-sm font-mono font-semibold">100%</p>
                </div>
            </div>
        </div>
    );
}

function EcomDashboardStep({ data, view, setView, onReopenConsent }) {
    const showConsumption = view === "alternative";
    const shownScore = showConsumption ? data.full_alternative_score : data.traditional_cibil;
    const maxShown = 900;
    const pieData = [
        { pct: data.spending_breakdown.essentials.pct, color: "#3b82f6", label: data.spending_breakdown.essentials.label, amount: data.spending_breakdown.essentials.amount },
        { pct: data.spending_breakdown.food_delivery.pct, color: "#f59e0b", label: data.spending_breakdown.food_delivery.label, amount: data.spending_breakdown.food_delivery.amount },
        { pct: data.spending_breakdown.discretionary.pct, color: "#8b5cf6", label: data.spending_breakdown.discretionary.label, amount: data.spending_breakdown.discretionary.amount },
    ];
    return (
        <div data-testid="step-ecom-dashboard">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-text-primary mb-2">Your Consumption Score Breakdown</h2>
            <p className="text-text-secondary text-sm mb-5">Here's what your spending patterns reveal about your financial behavior.</p>

            {/* Hero: Monthly spend */}
            <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="relative overflow-hidden p-5 rounded-2xl border border-blue-primary/20 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-cyan-500/10 mb-4"
            >
                <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-1">Monthly Ecom Spend Capacity</p>
                <div className="flex items-baseline gap-2 mb-1">
                    <AnimatedCounter target={data.monthly_spend_avg} prefix="₹" className="font-mono text-4xl sm:text-5xl font-bold text-text-primary tracking-tighter" />
                    <span className="text-text-secondary text-xs">/ month avg</span>
                </div>
                <p className="text-text-secondary text-[11px] mb-3">last 90 days · from {data.spend_trend_6mo.length} sources</p>
                <Sparkline points={data.spend_trend_6mo} color="#3b82f6" />
                <div className="flex items-center justify-between mt-1 text-[10px] text-text-secondary">
                    <span>6 mo ago: ₹{data.spend_trend_6mo[0].toLocaleString("en-IN")}</span>
                    <span className="text-green-pioneer font-semibold">↑ +{Math.round(((data.spend_trend_6mo[5] - data.spend_trend_6mo[0]) / data.spend_trend_6mo[0]) * 100)}%</span>
                </div>
            </motion.div>

            {/* Spending Pattern */}
            <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="p-5 rounded-2xl border border-white/5 bg-white/[0.02] mb-4"
            >
                <p className="text-text-primary text-sm font-semibold mb-3">Spending Pattern</p>
                {/* Bug 7: flex-col on mobile so legend never gets clipped beside the pie */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                    <div className="shrink-0 mx-auto sm:mx-0">
                        <ConicPie data={pieData} size={120} />
                    </div>
                    <div className="flex-1 space-y-1.5">
                        {pieData.map(p => (
                            <div key={p.label} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: p.color }} />
                                    <span className="text-text-secondary">{p.label}</span>
                                </div>
                                <span className="text-text-primary font-mono font-semibold shrink-0 ml-2">{p.pct}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Signal Badges */}
            <div className="mb-4">
                <p className="text-text-primary text-sm font-semibold mb-2">Key Consumption Signals</p>
                <div className="space-y-2">
                    {data.signals.map((sig, i) => (
                        <motion.div
                            key={sig.name}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + i * 0.08, duration: 0.3 }}
                            whileHover={{ scale: 1.02 }}
                            className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border-l-2 hover:bg-white/[0.04] transition-all"
                            style={{ borderLeftColor: sig.color }}
                            data-testid={`ecom-signal-${i}`}
                        >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${sig.color}20` }}>
                                <Award size={14} style={{ color: sig.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-text-primary text-sm font-medium leading-tight">{sig.name}</p>
                                <p className="text-text-secondary text-[11px] mt-0.5">{sig.detail}</p>
                            </div>
                            <div className="shrink-0 text-right">
                                <p className="font-mono text-sm font-bold" style={{ color: sig.color }}>+{sig.points}</p>
                                <p className="text-[10px] text-text-secondary/60">pts</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Score Impact */}
            <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
                className="p-5 rounded-2xl border border-white/5 bg-white/[0.02] mb-4"
            >
                <div className="flex items-center justify-between mb-3">
                    <p className="text-text-primary text-sm font-semibold">Score Impact</p>
                    <div className="flex bg-white/5 rounded-lg p-0.5">
                        <button
                            onClick={() => setView("traditional")}
                            className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${view === "traditional" ? "bg-white/10 text-text-primary" : "text-text-secondary"}`}
                            data-testid="score-view-traditional"
                        >Traditional CIBIL</button>
                        <button
                            onClick={() => setView("alternative")}
                            className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${view === "alternative" ? "bg-blue-primary text-white" : "text-text-secondary"}`}
                            data-testid="score-view-alternative"
                        >Full Alternative ★</button>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <div className="flex items-baseline justify-between mb-1">
                            <span className="text-text-secondary text-xs">Traditional CIBIL</span>
                            <span className="font-mono text-lg font-bold text-amber-builder">{data.traditional_cibil}</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${(data.traditional_cibil / maxShown) * 100}%` }}
                                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                                className="h-full bg-gradient-to-r from-red-starter to-amber-builder"
                            />
                        </div>
                    </div>

                    <AnimatePresence>
                        {showConsumption && (
                            <motion.div
                                key="alt"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-baseline justify-between mb-1">
                                    <span className="text-text-secondary text-xs">Full Alternative Score</span>
                                    <AnimatedCounter target={data.full_alternative_score} duration={1600} className="font-mono text-lg font-bold text-green-pioneer" />
                                </div>
                                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(data.full_alternative_score / maxShown) * 100}%` }}
                                        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                                        className="h-full bg-gradient-to-r from-blue-primary via-cyan-signal to-green-pioneer"
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {showConsumption && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="flex items-center gap-2 p-3 rounded-xl bg-green-pioneer/10 border border-green-pioneer/20"
                            >
                                <TrendingUp size={16} className="text-green-pioneer" />
                                <div className="flex-1">
                                    <p className="text-text-primary text-sm font-semibold">
                                        Uplift: <AnimatedCounter target={data.total_consumption_uplift} duration={1500} className="font-mono text-green-pioneer" prefix="+" suffix=" pts" />
                                    </p>
                                    <p className="text-text-secondary text-[11px]">Your real spending power is much higher than traditional credit scores show.</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            <div className="flex items-center gap-3">
                <button onClick={onReopenConsent} className="flex-1 text-text-secondary hover:text-text-primary border border-white/10 hover:border-white/20 px-4 py-2.5 rounded-xl transition-all text-xs font-medium" data-testid="ecom-reopen-consent">
                    Connect More Accounts
                </button>
            </div>
        </div>
    );
}

/* ═══════════════ MAIN WIZARD ═══════════════ */
export default function OnboardingWizard() {
    const { user, login, register } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [dir, setDir] = useState(1);

    // Step 1 auth
    const [authTab, setAuthTab] = useState("signin");
    const [authForm, setAuthForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [authError, setAuthError] = useState("");
    const [authLoading, setAuthLoading] = useState(false);
    const [authShowPw, setAuthShowPw] = useState(false);

    // Step 2 consent
    const [consents, setConsents] = useState({ academic_financial: true, developer_professional: true, social_creator: false, identity_optional: false, digilocker_identity: false });

    // Step 3 academic
    const [academicTab, setAcademicTab] = useState("iwebtechno");
    const [academic, setAcademic] = useState({ college: "", course: "", category: "engineering", year: 3, cgpa: "", scale: "10", trend: "stable", attendance: "" });
    const [academicFile, setAcademicFile] = useState(null);
    const [iwebtechnoAcademicSaved, setIwebtechnoAcademicSaved] = useState(false);
    const [digilockerModalOpen, setDigilockerModalOpen] = useState(false);

    // Step 4 fee
    const [feeTab, setFeeTab] = useState("iwebtechno");
    const [fee, setFee] = useState({ pattern: "semester", timing: "on_time", payer: "parent", loanBank: "", annualFee: "" });
    const [feeFile, setFeeFile] = useState(null);
    const [iwebtechnoFeeSaved, setIwebtechnoFeeSaved] = useState(false);
    const hasManualFeeData = Boolean(
        fee.annualFee ||
        fee.loanBank ||
        fee.pattern !== "semester" ||
        fee.timing !== "on_time" ||
        fee.payer !== "parent"
    );

    // Step 5 profiles
    const [profileStatus, setProfileStatus] = useState({});
    // Bug 3: persist fetched profile data in parent so PlatformCard remounts with data intact
    const [profileSavedData, setProfileSavedData] = useState({});
    const [profileFetchingCount, setProfileFetchingCount] = useState(0); // tracks active fetches

    // Step 6 documents
    const [docs, setDocs] = useState({ resume: null, marksheets: null, receipts: null, other: null });

    // Step 7 ecom consent
    const [ecomConsent, setEcomConsent] = useState(false);
    const [ecomConnections, setEcomConnections] = useState({ amazon_flipkart: null, food_delivery: null, quick_commerce: null, bank_aa: null });

    // Step 8 ecom dashboard
    const [scoreView, setScoreView] = useState("alternative"); // "traditional" or "alternative"
    const [ecomDataSaved, setEcomDataSaved] = useState(false);

    // Step 9
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState(null);
    const visibleSteps = STEPS
        .map((item, index) => ({ ...item, originalIndex: index }))
        .filter(item => {
            if ((item.originalIndex === 2 || item.originalIndex === 3) && !consents.academic_financial) return false;
            if (item.originalIndex === 7 && !ecomConsent) return false;
            return true;
        });
    const visibleStepIndex = Math.max(0, visibleSteps.findIndex(item => item.originalIndex === step));

    // On mount, check if user already logged in to skip step 1
    useEffect(() => {
        if (user && step === 0) {
            // stay on welcome, show continue
        }
    }, [user, step]);

    // Check if user has github connected
    const githubConnected = user?.github_connected;
    useEffect(() => {
        if (githubConnected) setProfileStatus(p => ({ ...p, github: "oauth" }));
    }, [githubConnected]);

    // ── Bug 8: sessionStorage persistence ────────────────────────────────────
    // On mount, restore wizard progress if user is logged in and was mid-flow.
    // Only core form fields are saved — scrape results are re-fetched as needed.
    useEffect(() => {
        if (!user) return;
        try {
            const raw = sessionStorage.getItem("wizard_progress");
            if (!raw) return;
            const saved = JSON.parse(raw);
            // Validate: must be a recent non-zero step from this session
            if (saved.step && saved.step > 0 && saved.step < 8) {
                setStep(saved.step);
                if (saved.consents) setConsents(c => ({ ...c, ...saved.consents }));
                if (saved.academic) setAcademic(a => ({ ...a, ...saved.academic }));
                if (saved.fee) setFee(f => ({ ...f, ...saved.fee }));
            }
        } catch (_) { /* ignore stale/corrupt data */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Save progress on every meaningful state change (step, academic, fee, consents)
    useEffect(() => {
        if (!user || step === 0) return; // don't persist before auth
        try {
            sessionStorage.setItem("wizard_progress", JSON.stringify({
                step,
                consents,
                academic: { college: academic.college, course: academic.course, category: academic.category, year: academic.year, cgpa: academic.cgpa, scale: academic.scale },
                fee: { pattern: fee.pattern, timing: fee.timing, payer: fee.payer },
            }));
        } catch (_) { /* ignore storage quota errors */ }
    // Every field written above must be listed, or changing only the year,
    // category or scale silently failed to persist and was lost on reload.
    }, [user, step, consents, academic.college, academic.course, academic.category,
        academic.year, academic.cgpa, academic.scale, fee.pattern, fee.timing, fee.payer]);
    // ─────────────────────────────────────────────────────────────────────────

    const goNext = () => { setDir(1); setStep(s => s + 1); };
    const goBack = () => { setDir(-1); setStep(s => s - 1); };

    // BUG-013: effectiveStep was computed but never consumed — removed dead code.
    // Step-skipping logic lives entirely in handleNext/handleBack below.

    const handleNext = async () => {
        if (step === 0 && !user) { toast.error("Please sign in or create an account first"); return; }
        if (step === 1) { await saveConsent(); }
        if (step === 2) { await saveAcademic(); }
        if (step === 3) { await saveFee(); }
        if (step === 4) { await saveProfiles(); }
        if (step === 5) { await saveDocs(); }
        if (step === 6) { await saveEcomConsent(); }
        if (step === 7) { await saveEcomConsumption(); }

        let next = step + 1;
        // Skip academic/fee steps if consent off
        if (next === 2 && !consents.academic_financial) next = 4;
        if (next === 3 && !consents.academic_financial) next = 4;
        // Skip ecom dashboard if user didn't consent
        if (next === 7 && !ecomConsent) next = 8;
        setDir(1);
        setStep(next);
    };

    const handleBack = () => {
        let prev = step - 1;
        if (prev === 3 && !consents.academic_financial) prev = 1;
        if (prev === 2 && !consents.academic_financial) prev = 1;
        if (prev === 7 && !ecomConsent) prev = 6;
        setDir(-1);
        setStep(prev);
    };

    /* ─── Step handlers ─── */
    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthError("");
        setAuthLoading(true);

        // --- client-side validation ---
        if (authTab === "register") {
            if (!authForm.name.trim()) { setAuthError("Please enter your full name"); setAuthLoading(false); return; }
            if (!authForm.email.includes("@") || !authForm.email.includes(".")) { setAuthError("Please enter a valid email address"); setAuthLoading(false); return; }
            if (authForm.password !== authForm.confirm) { setAuthError("Passwords don't match"); setAuthLoading(false); return; }
            if (authForm.password.length < 8) { setAuthError("Password must be at least 8 characters"); setAuthLoading(false); return; }
            if (!/[A-Za-z]/.test(authForm.password) || !/\d/.test(authForm.password)) { setAuthError("Password must include at least one letter and one number"); setAuthLoading(false); return; }
            if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(authForm.password)) { setAuthError("Password must include at least one special character (e.g. @, #, !)"); setAuthLoading(false); return; }
        }

        try {
            if (authTab === "signin") {
                await login(authForm.email, authForm.password);
            } else {
                await register(authForm.email, authForm.password, authForm.name);
            }
            toast.success("Welcome!");
            setTimeout(goNext, 400);
        } catch (err) {
            const status = err.response?.status;
            const d = err.response?.data?.detail;
            if (!err.response) {
                setAuthError("Connection error. Check your internet and try again.");
            } else if (status === 429) {
                setAuthError("Too many failed attempts. Please wait 15 minutes before trying again.");
            } else if (typeof d === "string") {
                setAuthError(d);
            } else if (Array.isArray(d)) {
                setAuthError(d.map(e => e.msg || String(e)).join(" "));
            } else {
                setAuthError("Authentication failed. Please check your credentials.");
            }
        }
        setAuthLoading(false);
    };

    const saveConsent = async () => {
        try {
            const payload = {};
            Object.entries(consents).forEach(([key, granted]) => {
                payload[key] = { granted };
            });
            await api.post("/api/consent", payload);
        } catch { /* ignore */ }
    };

    const saveAcademic = async () => {
        if (!consents.academic_financial) return;
        // If iWebTechno placeholder was used, the evidence is already saved via its own handler. Skip.
        if (academicTab === "iwebtechno" && iwebtechnoAcademicSaved) return;
        // Manual/upload tab
        if (academicTab !== "manual" && academicTab !== "upload") return;
        const isUpload = academicTab === "upload" && academicFile;
        try {
            await api.post("/api/evidence", {
                provider: "academic",
                source_type: isUpload ? "uploaded_document" : "user_declared",
                user_facing_label: isUpload ? "Academic document uploaded" : "Academic data entered",
                confidence: isUpload ? 0.55 : 0.30,
                consent_scope: "academic_financial",
                data: {
                    institution: academic.college, course: academic.course, category: academic.category,
                    year: Number(academic.year), cgpa: Number(academic.cgpa) || null, scale: academic.scale,
                    grade_trend: academic.trend, attendance: Number(academic.attendance) || null,
                    ...(isUpload ? { file_name: academicFile.name, file_size: academicFile.size } : {}),
                },
            });
        } catch { /* ignore */ }
    };

    const saveIWebTechnoAcademic = async (mock) => {
        try {
            await api.post("/api/evidence", {
                provider: "academic",
                source_type: "verified_system",
                user_facing_label: "iWebTechno ERP — Academic records fetched",
                confidence: FF_IWEBTECHNO_DIRECT ? 0.95 : 0.75,
                consent_scope: "academic_financial",
                data: { ...mock, _source: "iwebtechno_erp" },
            });
            setIwebtechnoAcademicSaved(true);
            // Pre-fill form state for the Review page summary
            setAcademic(p => ({
                ...p,
                college: mock.institution, course: mock.course, category: mock.category,
                year: mock.year, cgpa: String(mock.cgpa), scale: mock.scale,
                trend: mock.grade_trend, attendance: String(mock.attendance),
            }));
            toast.success("iWebTechno academic records saved");
        } catch {
            toast.error("Failed to save iWebTechno records");
        }
    };

    const saveIWebTechnoFee = async (mock) => {
        try {
            await api.post("/api/evidence", {
                provider: "fee_payment",
                source_type: "verified_system",
                user_facing_label: "iWebTechno ERP — Fee payment ledger fetched",
                confidence: FF_IWEBTECHNO_DIRECT ? 0.95 : 0.75,
                consent_scope: "academic_financial",
                data: { ...mock, _source: "iwebtechno_erp" },
            });
            setIwebtechnoFeeSaved(true);
            setFee(p => ({
                ...p,
                pattern: mock.pattern, timing: mock.timing, payer: mock.payer,
                annualFee: String(mock.annual_fee),
            }));
            toast.success("iWebTechno fee ledger saved");
        } catch {
            toast.error("Failed to save iWebTechno fee data");
        }
    };

    const saveFee = async () => {
        if (!consents.academic_financial) return;
        if (feeTab === "iwebtechno" && iwebtechnoFeeSaved) return;
        if (feeTab !== "manual" && feeTab !== "upload") return;
        const isUpload = feeTab === "upload" && feeFile;
        if (!isUpload && !hasManualFeeData) return;
        try {
            await api.post("/api/evidence", {
                provider: "fee_payment",
                source_type: isUpload ? "uploaded_document" : "user_declared",
                user_facing_label: isUpload ? "Fee receipt uploaded" : "Fee payment data entered",
                confidence: isUpload ? 0.55 : 0.30,
                consent_scope: "academic_financial",
                data: {
                    pattern: fee.pattern, timing: fee.timing, payer: fee.payer,
                    loan_detected: fee.payer === "loan", loan_bank: fee.loanBank || null,
                    annual_fee: Number(fee.annualFee) || null,
                    ...(isUpload ? { file_name: feeFile.name, file_size: feeFile.size } : {}),
                },
            });
        } catch { /* ignore */ }
    };

    const saveEcomConsent = async () => {
        try {
            await api.post("/api/consent", {
                ecom_consumption: { granted: ecomConsent },
            });
        } catch { /* ignore */ }
    };

    const saveEcomConsumption = async () => {
        if (!ecomConsent || ecomDataSaved) return;
        try {
            await api.post("/api/evidence", {
                provider: "ecom_consumption",
                source_type: ecomConnections.bank_aa ? "account_aggregator" : "mock",
                user_facing_label: "Ecom spending analysis",
                confidence: ecomConnections.bank_aa ? 0.7 : 0.4,
                consent_scope: "ecom_consumption",
                data: { ...ECOM_MOCK_DATA, connections: ecomConnections },
            });
            setEcomDataSaved(true);
        } catch { /* ignore */ }
    };

    const saveProfiles = async () => {
        // Profiles are saved individually when user clicks save on each card
    };

    const handleProfileSave = async (platform, { type, data, confidence }) => {
        const scopeMap = { github: "developer_professional", linkedin: "developer_professional", portfolio: "developer_professional", youtube: "social_creator", twitter: "social_creator", instagram: "social_creator", reddit: "social_creator" };
        if (type === "url_ingest") {
            setProfileStatus(p => ({ ...p, [platform]: "url" }));
            // Bug 3: store fetched data in parent so PlatformCard remounts with it
            if (data) setProfileSavedData(p => ({ ...p, [platform]: data }));
            return;
        }
        try {
            await api.post("/api/evidence", {
                provider: platform, source_type: type,
                user_facing_label: `${platform} data submitted`,
                confidence, consent_scope: scopeMap[platform] || "identity_optional",
                data,
            });
            setProfileStatus(p => ({ ...p, [platform]: type === "url_ingest" ? "url" : "manual" }));
            toast.success(`${platform} saved`);
        } catch { toast.error(`Failed to save ${platform}`); }
    };

    const saveDocs = async () => {
        for (const [key, file] of Object.entries(docs)) {
            if (!file) continue;
            try {
                await api.post("/api/evidence", {
                    provider: "document", source_type: "uploaded_document",
                    user_facing_label: `${key} uploaded`, confidence: 0.55,
                    consent_scope: "identity_optional",
                    data: { document_type: key, file_name: file.name, file_size: file.size },
                });
            } catch { /* ignore */ }
        }
    };

    const handleGenerateScore = async () => {
        // Bug 2: pre-flight data check — don't allow generating with zero evidence.
        // At least one consent must be enabled AND at least one piece of data entered.
        const hasAnyEvidence =
            (academic.college || academic.cgpa || academicFile || iwebtechnoAcademicSaved) ||
            (hasManualFeeData || feeFile || iwebtechnoFeeSaved) ||
            Object.keys(profileStatus).length > 0 ||
            Object.values(docs).some(Boolean) ||
            (ecomConsent && Object.values(ecomConnections).some(v => v === "connected"));

        if (!hasAnyEvidence) {
            const msg = "No information entered yet. Please add your academic or fee details, or connect one profile, before generating your score.";
            // The overlay is the only thing that renders genError, so it has to be
            // open for the message to be visible. Without this the button looked
            // completely dead: state changed, nothing appeared on screen.
            setGenError(msg);
            setGenerating(true);
            toast.error(msg);
            return;
        }

        setGenerating(true);
        setGenError(null);

        // ── 1. Belt-and-suspenders: re-run any save handlers that may have been
        //    skipped (user using fast-forward navigation, or save fired before
        //    state had hydrated). Errors are swallowed silently so we never block
        //    score generation; the backend will tell us if evidence is missing.
        try {
            await Promise.allSettled([
                saveConsent(),
                saveAcademic(),
                saveFee(),
                saveDocs(),
                saveEcomConsent(),
                ecomConsent ? saveEcomConsumption() : Promise.resolve(),
            ]);
        } catch { /* ignore — backend is the source of truth */ }

        // ── 2. Helper: parse FastAPI error responses cleanly. Detail can be a
        //    string, a list of validation errors, or absent (network/CORS).
        const parseError = (err) => {
            // Network / CORS / timeout — no response object at all
            if (!err.response) {
                if (err.code === "ECONNABORTED") {
                    return "The AI engine is taking longer than expected. Please retry — your data is safe.";
                }
                if (err.message === "Network Error") {
                    return "Network error. Check your connection and retry.";
                }
                return `Request failed: ${err.message || "unknown error"}. Please retry.`;
            }
            const status = err.response.status;
            const data = err.response.data;
            // Auth — guide user to login
            if (status === 401) {
                return "Your session expired. Please sign in again to generate your score.";
            }
            const detail = data?.detail;
            if (typeof detail === "string") return detail;
            if (Array.isArray(detail) && detail.length) {
                return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
            }
            if (typeof detail === "object" && detail !== null) {
                return JSON.stringify(detail);
            }
            return `Score generation failed (HTTP ${status}). Please retry.`;
        };

        // ── 3. Two-stage call:
        //    a) Try the full /score/calculate (GenZGPT Agent explanation included, slow).
        //    b) If it fails for any reason OTHER than auth/missing-evidence,
        //       retry with ?fast=true to guarantee a result using the template fallback.
        const FULL_TIMEOUT_MS = 60000;   // Claude w/ extended thinking can take 30-45s
        const FAST_TIMEOUT_MS = 10000;

        const tryCalculate = async (fast = false) => {
            return api.post(
                `/api/score/calculate${fast ? "?fast=true" : ""}`,
                null,
                { timeout: fast ? FAST_TIMEOUT_MS : FULL_TIMEOUT_MS }
            );
        };

        try {
            const res = await tryCalculate(false);
            const scoreId = res.data?.id;
            if (!scoreId) throw new Error("Backend returned no score id");
            // Clear saved wizard progress now that the score is generated
            try { sessionStorage.removeItem("wizard_progress"); } catch (_) {}
            // Brief loading-overlay flourish before navigating
            setTimeout(() => navigate(`/genz-credit-scoring/result/${scoreId}`), 1800);
            return;
        } catch (err) {
            const status = err.response?.status;
            // Don't auto-retry auth or missing-evidence failures — those are user errors
            if (status === 401 || status === 400) {
                setGenError(parseError(err));
                setGenerating(false); // BUG-003: overlay was never dismissed on error
                return;
            }
            // Anything else: try the fast path (skip Claude) so the user still gets a score
            try {
                const res2 = await tryCalculate(true);
                const scoreId = res2.data?.id;
                if (!scoreId) throw new Error("Backend returned no score id");
                toast.warning("AI explanation skipped (rate-limited). Score still computed.");
                setTimeout(() => navigate(`/genz-credit-scoring/result/${scoreId}`), 1800);
            } catch (err2) {
                setGenError(parseError(err2));
                setGenerating(false); // BUG-003: ensure overlay dismisses on fallback error too
            }
        }
    };

    /* ─── Coverage calc (6 categories with ecom) ─── */
    const coverage = (() => {
        let c = 0;
        if (consents.academic_financial && (academic.college || academicFile || iwebtechnoAcademicSaved)) c++;
        // BUG-012: was `fee.pattern !== "semester"` which is inverted — "semester" IS the
        // most common valid pattern and should count. Fixed to check if any fee data exists.
        if (consents.academic_financial && (hasManualFeeData || feeFile || iwebtechnoFeeSaved)) c++;
        const devPlatforms = ["github", "linkedin", "portfolio"];
        const socPlatforms = ["youtube", "twitter", "instagram"];
        if (devPlatforms.some(p => profileStatus[p])) c++;
        if (socPlatforms.some(p => profileStatus[p])) c++;
        if (Object.values(docs).some(Boolean)) c++;
        if (ecomConsent && Object.values(ecomConnections).some(v => v === "connected")) c++;
        return c;
    })();

    /* ─── Render Steps ─── */
    const renderStep = () => {
        switch (step) {
            case 0: return (
                <div data-testid="step-welcome">
                    <StepHeading title="Let's Build Your Credit Profile" subtitle="This takes about 5 minutes. You control what you share." />
                    <div className="flex gap-3 mb-8 flex-wrap">
                        {[["Lock", "Encrypted"], ["Eye", "You Control Your Data"], ["Brain", "AI-Powered Analysis"]].map(([icon, text]) => (
                            <div key={text} className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] border border-white/5 rounded-lg text-xs text-text-secondary">
                                {icon === "Lock" && <Lock size={13} />}{icon === "Eye" && <Eye size={13} />}{icon === "Brain" && <Brain size={13} />}
                                {text}
                            </div>
                        ))}
                    </div>
                    {user ? (
                        <div>
                            <p className="text-text-primary text-lg mb-4">Welcome back, <span className="font-semibold text-blue-primary">{user.name || user.email}</span>!</p>
                            <button onClick={goNext} className="bg-blue-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-primary/90 transition-all inline-flex items-center gap-2" data-testid="welcome-continue">
                                Continue <ChevronRight size={18} />
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div className="flex gap-1 mb-5 bg-white/5 rounded-lg p-1 w-fit">
                                {["signin", "register"].map(t => (
                                    <button key={t} onClick={() => setAuthTab(t)}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${authTab === t ? "bg-blue-primary text-white" : "text-text-secondary hover:text-text-primary"}`}
                                        data-testid={`auth-tab-${t}`}>
                                        {t === "signin" ? "Sign In" : "Create Account"}
                                    </button>
                                ))}
                            </div>
                            {authError && (
                                <div className="flex items-center gap-2 bg-red-starter/10 border border-red-starter/20 rounded-xl px-4 py-2.5 mb-4">
                                    <AlertCircle size={14} className="text-red-starter shrink-0" />
                                    <p className="text-xs text-red-starter">{authError}</p>
                                </div>
                            )}
                            <form onSubmit={handleAuth} className="space-y-3">
                                {authTab === "register" && (
                                    <div className="glass-input-wrap">
                                        <User size={15} className="field-icon" />
                                        <input type="text" placeholder="Full name" value={authForm.name} onChange={e => setAuthForm(p => ({ ...p, name: e.target.value }))}
                                            className="glass-input text-sm" required data-testid="auth-name" />
                                    </div>
                                )}
                                <div className="glass-input-wrap">
                                    <Mail size={15} className="field-icon" />
                                    <input type="email" placeholder="Email" value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))}
                                        className="glass-input text-sm" required data-testid="auth-email" />
                                </div>
                                <div className="glass-input-wrap">
                                    <Lock size={15} className="field-icon" />
                                    <input type={authShowPw ? "text" : "password"} placeholder="Password" value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))}
                                        className="glass-input text-sm" required data-testid="auth-password" />
                                    <button type="button" onClick={() => setAuthShowPw(v => !v)} className="eye-toggle" tabIndex={-1}>
                                        {authShowPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                    </button>
                                </div>
                                {/* Bug 19: password strength checklist for register */}
                                {authTab === "register" && authForm.password.length > 0 && (
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 px-1 text-[11px]">
                                        {[
                                            [authForm.password.length >= 8, "8+ characters"],
                                            [/[A-Za-z]/.test(authForm.password), "One letter"],
                                            [/\d/.test(authForm.password), "One number"],
                                            [/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(authForm.password), "Special char (!@#…)"],
                                        ].map(([ok, label]) => (
                                            <span key={label} className={`flex items-center gap-1 ${ok ? "text-green-pioneer" : "text-text-secondary/60"}`}>
                                                {ok ? "✓" : "○"} {label}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {authTab === "register" && (
                                    <div className="glass-input-wrap">
                                        <Lock size={15} className="field-icon" />
                                        <input type="password" placeholder="Confirm password" value={authForm.confirm} onChange={e => setAuthForm(p => ({ ...p, confirm: e.target.value }))}
                                            className="glass-input text-sm" required data-testid="auth-confirm" />
                                    </div>
                                )}
                                {authTab === "signin" && (
                                    <div className="text-right -mt-1">
                                        <Link to="/forgot-password" className="text-[11px] text-blue-primary/80 hover:text-blue-primary transition-colors">
                                            Forgot password?
                                        </Link>
                                    </div>
                                )}
                                <button type="submit" disabled={authLoading}
                                    className="w-full bg-blue-primary text-white py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all disabled:opacity-50"
                                    data-testid="auth-submit">
                                    {authLoading ? "Please wait..." : authTab === "signin" ? "Sign In" : "Create Account"}
                                </button>
                            </form>
                            {/* Bug 4: same return-path fix for inline auth panel */}
                            <button
                                type="button"
                                onClick={() => {
                                    sessionStorage.setItem("oauth_return_to", window.location.pathname + window.location.search);
                                    window.location.href = `${API_URL}/api/oauth/github/start`;
                                }}
                                className="mt-3 w-full flex items-center justify-center gap-2 bg-elevated text-text-primary border border-white/10 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-all"
                                data-testid="auth-github">
                                <Github size={16} /> Sign in with GitHub
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    sessionStorage.setItem("oauth_return_to", window.location.pathname + window.location.search);
                                    window.location.href = `${API_URL}/api/oauth/google/start`;
                                }}
                                className="mt-2 w-full flex items-center justify-center gap-2 bg-elevated text-text-primary border border-white/10 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-all"
                                data-testid="auth-google">
                                <Globe size={16} /> Sign in with Google
                            </button>
                        </div>
                    )}
                </div>
            );

            case 1: return (
                <div data-testid="step-consent">
                    <StepHeading title="What Would You Like to Share?" subtitle="Everything is opt-in. You can change these later." />
                    <div className="space-y-3">
                        <ToggleCard icon={GraduationCap} title="Academic & Financial" desc="College records, grades, fee payment history" enabled={consents.academic_financial} onChange={v => setConsents(p => ({ ...p, academic_financial: v }))} />
                        <ToggleCard icon={Code2} title="Developer & Professional" desc="GitHub repos, LinkedIn profile, portfolio, resume" enabled={consents.developer_professional} onChange={v => setConsents(p => ({ ...p, developer_professional: v }))} />
                        <ToggleCard icon={Share2} title="Social & Creator" desc="YouTube, Twitter, Instagram presence" enabled={consents.social_creator} onChange={v => setConsents(p => ({ ...p, social_creator: v }))} />
                        <ToggleCard
                            icon={Landmark}
                            title="Government Identity (DigiLocker)"
                            desc="Access verified marksheets & ID docs from DigiLocker. Documents are fetched once and not stored."
                            enabled={consents.digilocker_identity}
                            onChange={v => setConsents(p => ({ ...p, digilocker_identity: v }))}
                            badge={FF_DIGILOCKER ? "GOV Verified" : "Coming Soon"}
                        />
                    </div>
                </div>
            );

            case 2: {
                const academicTabs = [
                    { key: "iwebtechno", label: "iWebTechno ERP", badge: "★" },
                    ...(consents.digilocker_identity ? [{ key: "digilocker", label: "DigiLocker", badge: "GOV" }] : []),
                    { key: "manual", label: "Enter Manually" },
                    { key: "upload", label: "Upload Document" },
                ];
                return (
                <div data-testid="step-academic">
                    <StepHeading title="Academic Information" subtitle="Pull verified records directly, or enter manually." />
                    <div className="flex gap-1 mb-5 bg-white/5 rounded-lg p-1 w-fit flex-wrap">
                        {academicTabs.map(t => (
                            <button key={t.key} onClick={() => setAcademicTab(t.key)}
                                className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all inline-flex items-center gap-1.5 ${academicTab === t.key ? "bg-blue-primary text-white" : "text-text-secondary hover:text-text-primary"}`}
                                data-testid={`academic-tab-${t.key}`}>
                                {t.label}
                                {t.badge && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${academicTab === t.key ? "bg-white/20 text-white" : t.badge === "GOV" ? "bg-amber-400/20 text-amber-300" : "bg-blue-primary/20 text-blue-primary"}`}>{t.badge}</span>}
                            </button>
                        ))}
                    </div>
                    {academicTab === "iwebtechno" && (
                        <IWebTechnoAcademicCard onUse={saveIWebTechnoAcademic} used={iwebtechnoAcademicSaved} />
                    )}
                    {academicTab === "digilocker" && (
                        <div className="p-6 bg-gradient-to-br from-amber-400/5 to-yellow-600/5 border border-amber-400/20 rounded-xl text-center" data-testid="digilocker-academic-panel">
                            <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-400/15 border border-amber-400/25 flex items-center justify-center mb-4">
                                <Landmark size={24} className="text-amber-300" />
                            </div>
                            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Connect DigiLocker</h3>
                            <p className="text-text-secondary text-xs mb-4 max-w-sm mx-auto">Access verified academic records directly from the Government of India's digital locker.</p>
                            <div className="text-left max-w-xs mx-auto mb-5 space-y-1.5">
                                {["10th & 12th marksheets (CBSE / State Board)", "Degree / provisional certificates", "Semester marksheets (if uploaded)", "Aadhaar verification status (number NOT stored)"].map(x => (
                                    <div key={x} className="flex items-start gap-2 text-xs text-text-secondary"><CheckCircle2 size={12} className="text-amber-300 mt-0.5 shrink-0" /><span>{x}</span></div>
                                ))}
                            </div>
                            <button onClick={() => {
                                if (FF_DIGILOCKER) {
                                    window.location.href = "https://digilocker.gov.in";
                                } else {
                                    setDigilockerModalOpen(true);
                                }
                            }} className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black px-5 py-3 rounded-xl font-semibold text-sm hover:shadow-[0_0_24px_rgba(251,191,36,0.4)] transition-all inline-flex items-center justify-center gap-2" data-testid="digilocker-connect-btn">
                                <Landmark size={15} /> Connect DigiLocker
                            </button>
                            <p className="text-[11px] text-text-secondary/70 mt-3 max-w-xs mx-auto">🔒 Documents are fetched once and not stored. Only extracted data is saved. Disconnect anytime.</p>
                        </div>
                    )}
                    {academicTab === "manual" && (
                        <div className="space-y-3">
                            <CollegeAutocomplete value={academic.college} onChange={val => setAcademic(p => ({ ...p, college: val }))} />
                            <input type="text" placeholder="Course name (e.g. Computer Science)" value={academic.course}
                                onChange={e => setAcademic(p => ({ ...p, course: e.target.value }))} className="glass-input text-sm" data-testid="acad-course" />
                            <div className="grid grid-cols-2 gap-3">
                                <select value={academic.category} onChange={e => setAcademic(p => ({ ...p, category: e.target.value }))} className="glass-input text-sm" data-testid="acad-category">
                                    <option value="engineering">Engineering</option>
                                    <option value="business">Business</option>
                                    <option value="arts_science">Arts & Science</option>
                                    <option value="medical">Medical</option>
                                    <option value="law">Law</option>
                                    <option value="other">Other</option>
                                </select>
                                <select value={academic.year} onChange={e => setAcademic(p => ({ ...p, year: e.target.value }))} className="glass-input text-sm" data-testid="acad-year">
                                    {[1, 2, 3, 4, 5].map(y => <option key={y} value={y}>Year {y}{y === 5 ? "+" : ""}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" step="0.1" placeholder="CGPA / Percentage" value={academic.cgpa}
                                    onChange={e => setAcademic(p => ({ ...p, cgpa: e.target.value }))} className="glass-input text-sm" data-testid="acad-cgpa" />
                                <select value={academic.scale} onChange={e => setAcademic(p => ({ ...p, scale: e.target.value }))} className="glass-input text-sm">
                                    <option value="10">Scale: 10.0</option>
                                    <option value="4">Scale: 4.0</option>
                                    <option value="100">Percentage</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <select value={academic.trend} onChange={e => setAcademic(p => ({ ...p, trend: e.target.value }))} className="glass-input text-sm">
                                    <option value="improving">Improving</option>
                                    <option value="stable">Stable</option>
                                    <option value="declining">Declining</option>
                                </select>
                                <input type="number" placeholder="Attendance % (optional)" value={academic.attendance}
                                    onChange={e => setAcademic(p => ({ ...p, attendance: e.target.value }))} className="glass-input text-sm" />
                            </div>
                        </div>
                    )}
                    {academicTab === "upload" && (
                        <FileDropZone file={academicFile} onFile={setAcademicFile} label="Upload your marksheet" />
                    )}
                </div>
            );}

            case 3: {
                const feeTabs = [
                    { key: "iwebtechno", label: "iWebTechno ERP", badge: "★" },
                    { key: "manual", label: "Enter Manually" },
                    { key: "upload", label: "Upload Receipt" },
                ];
                return (
                <div data-testid="step-fees">
                    <StepHeading title="Fee Payment History" subtitle="Help us understand your financial discipline." />
                    <div className="flex gap-1 mb-5 bg-white/5 rounded-lg p-1 w-fit flex-wrap">
                        {feeTabs.map(t => (
                            <button key={t.key} onClick={() => setFeeTab(t.key)}
                                className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all inline-flex items-center gap-1.5 ${feeTab === t.key ? "bg-blue-primary text-white" : "text-text-secondary hover:text-text-primary"}`}
                                data-testid={`fee-tab-${t.key}`}>
                                {t.label}
                                {t.badge && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${feeTab === t.key ? "bg-white/20 text-white" : "bg-blue-primary/20 text-blue-primary"}`}>{t.badge}</span>}
                            </button>
                        ))}
                    </div>
                    {feeTab === "iwebtechno" && (
                        <IWebTechnoFeeCard onUse={saveIWebTechnoFee} used={iwebtechnoFeeSaved} />
                    )}
                    {feeTab === "manual" && (
                        <div className="space-y-3">
                            <select value={fee.pattern} onChange={e => setFee(p => ({ ...p, pattern: e.target.value }))} className="glass-input text-sm" data-testid="fee-pattern">
                                <option value="lump_sum">Lump Sum</option>
                                <option value="semester">Semester</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="monthly">Monthly</option>
                                <option value="irregular">Irregular</option>
                            </select>
                            <select value={fee.timing} onChange={e => setFee(p => ({ ...p, timing: e.target.value }))} className="glass-input text-sm" data-testid="fee-timing">
                                <option value="always_on_time">Always On Time</option>
                                <option value="mostly_on_time">Mostly On Time</option>
                                <option value="sometimes_late">Sometimes Late</option>
                                <option value="often_late">Often Late</option>
                            </select>
                            <select value={fee.payer} onChange={e => setFee(p => ({ ...p, payer: e.target.value }))} className="glass-input text-sm" data-testid="fee-payer">
                                <option value="self">Self-funded</option>
                                <option value="scholarship">Scholarship</option>
                                <option value="parent">Parent-funded</option>
                                <option value="loan">Education Loan</option>
                            </select>
                            {fee.payer === "loan" && (
                                <input type="text" placeholder="Bank / NBFC name" value={fee.loanBank}
                                    onChange={e => setFee(p => ({ ...p, loanBank: e.target.value }))} className="glass-input text-sm" data-testid="fee-bank" />
                            )}
                            <input type="number" placeholder="Annual fee amount (optional)" value={fee.annualFee}
                                onChange={e => setFee(p => ({ ...p, annualFee: e.target.value }))} className="glass-input text-sm" />
                        </div>
                    )}
                    {feeTab === "upload" && (
                        <FileDropZone file={feeFile} onFile={setFeeFile} label="Upload your fee receipt" />
                    )}
                </div>
            );}

            case 4: return (
                <div data-testid="step-profiles">
                    <StepHeading title="Connect Your Digital Footprint" subtitle="Each connection strengthens your credit profile." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {consents.developer_professional && <PlatformCard name="github" icon={Github} color="#f0f6fc" onSave={(d) => handleProfileSave("github", d)} savedStatus={profileStatus.github} initialFetchedData={profileSavedData.github} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {consents.developer_professional && <PlatformCard name="linkedin" icon={Globe} color="#0a66c2" onSave={(d) => handleProfileSave("linkedin", d)} savedStatus={profileStatus.linkedin} initialFetchedData={profileSavedData.linkedin} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {consents.social_creator && <PlatformCard name="youtube" icon={Share2} color="#ff0000" onSave={(d) => handleProfileSave("youtube", d)} savedStatus={profileStatus.youtube} initialFetchedData={profileSavedData.youtube} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {consents.social_creator && <PlatformCard name="twitter" icon={Share2} color="#1da1f2" onSave={(d) => handleProfileSave("twitter", d)} savedStatus={profileStatus.twitter} initialFetchedData={profileSavedData.twitter} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {consents.social_creator && <PlatformCard name="instagram" icon={Share2} color="#e4405f" onSave={(d) => handleProfileSave("instagram", d)} savedStatus={profileStatus.instagram} initialFetchedData={profileSavedData.instagram} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {/* Reddit hidden — cloud IP blocks cause consistent fetch failures */}
                        {consents.developer_professional && <PlatformCard name="portfolio" icon={Globe} color="#8b5cf6" onSave={(d) => handleProfileSave("portfolio", d)} savedStatus={profileStatus.portfolio} initialFetchedData={profileSavedData.portfolio} onFetchStart={() => setProfileFetchingCount(c => c + 1)} onFetchEnd={() => setProfileFetchingCount(c => Math.max(0, c - 1))} />}
                        {!consents.developer_professional && !consents.social_creator && (
                            <p className="text-text-secondary text-sm text-center py-8 col-span-2">No profile connections available. Go back and enable Developer or Social consent.</p>
                        )}
                    </div>
                </div>
            );

            case 5: return (
                <div data-testid="step-documents">
                    <StepHeading title="Additional Documents (Optional)" subtitle="Upload any supporting documents to strengthen your profile." />
                    <p className="text-xs text-amber-builder/90 bg-amber-builder/10 border border-amber-builder/20 rounded-xl px-3 py-2 mb-4">
                        Current build stores document metadata only. Do not upload sensitive originals until secure document storage is enabled.
                    </p>
                    <div className="space-y-4">
                        <div><p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Resume</p>
                            <FileDropZone file={docs.resume} onFile={f => setDocs(p => ({ ...p, resume: f }))} accept=".pdf,.docx" label="Upload your resume (PDF/DOCX)" /></div>
                        <div><p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Additional Marksheets</p>
                            <FileDropZone file={docs.marksheets} onFile={f => setDocs(p => ({ ...p, marksheets: f }))} label="Upload additional marksheets" /></div>
                        <div><p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Fee Receipts</p>
                            <FileDropZone file={docs.receipts} onFile={f => setDocs(p => ({ ...p, receipts: f }))} label="Upload fee receipts" /></div>
                        <div><p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Other Documents</p>
                            <FileDropZone file={docs.other} onFile={f => setDocs(p => ({ ...p, other: f }))} label="Upload other supporting documents" /></div>
                    </div>
                </div>
            );

            case 6: return (
                <div data-testid="step-ecom-consent">
                    <StepHeading title="Boost Your Score with Real Spending Data" subtitle="Your spending patterns tell a powerful story. Connect your e-commerce accounts to unlock up to +150 bonus points." />
                    <p className="text-xs text-cyan-signal/90 bg-cyan-signal/10 border border-cyan-signal/20 rounded-xl px-3 py-2 mb-4">
                        Demo mode: these connectors simulate e-commerce signals until live Account Aggregator integrations are enabled.
                    </p>

                    <div className="space-y-2.5 mb-5">
                        {[
                            { key: "amazon_flipkart", icon: "🛒", title: "Demo: Amazon + Flipkart", desc: "Simulated purchase history, Prime status, order frequency, return rate", color: "from-orange-500/15 to-blue-500/10", border: "border-orange-400/20" },
                            { key: "food_delivery", icon: "🍕", title: "Demo: Zomato / Swiggy", desc: "Simulated food delivery frequency, avg order value, payment consistency", color: "from-red-500/15 to-orange-500/10", border: "border-red-400/20" },
                            { key: "quick_commerce", icon: "⚡", title: "Demo: Zepto / Blinkit", desc: "Simulated quick-commerce habits, daily essentials, subscription patterns", color: "from-purple-500/15 to-yellow-500/10", border: "border-purple-400/20" },
                            { key: "bank_aa", icon: "🏦", title: "Demo: Bank Account Aggregator", desc: "Simulated AA-style spending coverage — most comprehensive demo signal", color: "from-blue-500/15 to-cyan-500/10", border: "border-blue-400/25" },
                        ].map((btn, i) => {
                            const connected = !!ecomConnections[btn.key];
                            return (
                                <motion.button
                                    key={btn.key}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                    whileHover={{ scale: connected ? 1 : 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => {
                                        if (connected) return;
                                        // Optimistic: simulate connecting
                                        setEcomConnections(p => ({ ...p, [btn.key]: "connecting" }));
                                        setTimeout(() => {
                                            setEcomConnections(p => ({ ...p, [btn.key]: "connected" }));
                                            setEcomConsent(true);
                                            toast.success(`${btn.title.replace("Connect ", "")} connected`);
                                        }, 1400);
                                    }}
                                    className={`w-full text-left p-4 rounded-2xl border bg-gradient-to-br ${btn.color} ${btn.border} hover:shadow-[0_0_32px_rgba(59,130,246,0.15)] transition-all flex items-center gap-3 disabled:opacity-70`}
                                    data-testid={`ecom-btn-${btn.key}`}
                                    disabled={ecomConnections[btn.key] === "connecting"}
                                >
                                    <div className="text-2xl shrink-0">{btn.icon}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-text-primary text-sm font-semibold leading-tight">{btn.title}</p>
                                        <p className="text-text-secondary text-xs mt-0.5 leading-snug">{btn.desc}</p>
                                    </div>
                                    <div className="shrink-0">
                                        {ecomConnections[btn.key] === "connecting" ? (
                                            <Loader2 size={18} className="animate-spin text-blue-primary" />
                                        ) : connected ? (
                                            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-green-pioneer/20 text-green-pioneer inline-flex items-center gap-1"><CheckCircle2 size={10} /> Connected</span>
                                        ) : (
                                            <ChevronRight size={18} className="text-text-secondary/50" />
                                        )}
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>

                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl mb-5">
                        <div className="flex items-start gap-2">
                            <Lock size={14} className="text-text-secondary/70 mt-0.5 shrink-0" />
                            <div className="text-xs text-text-secondary leading-relaxed">
                                <span className="text-text-primary font-semibold">Privacy Note:</span> We analyze spending <em>patterns</em> only — categories, frequency, consistency. We never see item names, delivery addresses, or personal details. Your data is encrypted and you can disconnect anytime.
                            </div>
                        </div>
                    </div>

                    <label className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-xl cursor-pointer hover:bg-white/[0.04] transition-all">
                        <input type="checkbox" checked={ecomConsent} onChange={(e) => setEcomConsent(e.target.checked)}
                            className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-primary focus:ring-blue-primary/40"
                            data-testid="ecom-consent-toggle" />
                        <span className="text-xs text-text-primary">I consent to sharing my e-commerce spending data for credit analysis.</span>
                    </label>
                </div>
            );

            case 7: return (
                <EcomDashboardStep
                    data={ECOM_MOCK_DATA}
                    view={scoreView}
                    setView={setScoreView}
                    onReopenConsent={() => { setDir(-1); setStep(6); }}
                />
            );

            case 8: return (
                <div data-testid="step-review">
                    <StepHeading title="Review Your Profile" subtitle="Here's a summary of what you've provided." />

                    <div className="space-y-3 mb-6">
                        {/* Academic */}
                        <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                            <div className="flex items-center gap-2 mb-2">
                                <GraduationCap size={15} className="text-blue-primary" />
                                <span className="text-text-primary text-sm font-medium">Academic</span>
                                {iwebtechnoAcademicSaved && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary inline-flex items-center gap-1"><Database size={10} /> iWebTechno Verified</span>}
                            </div>
                            {academic.college ? (
                                <p className="text-text-secondary text-xs">{academic.college} — {academic.course || academic.category} — Year {academic.year}{academic.cgpa ? ` — CGPA ${academic.cgpa}` : ""}</p>
                            ) : academicFile ? (
                                <p className="text-text-secondary text-xs">Document uploaded: {academicFile.name}</p>
                            ) : (
                                <p className="text-text-secondary/50 text-xs">Not provided</p>
                            )}
                        </div>

                        {/* Fee */}
                        <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                            <div className="flex items-center gap-2 mb-2">
                                <CreditCard size={15} className="text-cyan-signal" />
                                <span className="text-text-primary text-sm font-medium">Fee Payment</span>
                                {iwebtechnoFeeSaved && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary inline-flex items-center gap-1"><Database size={10} /> iWebTechno Verified</span>}
                            </div>
                            {fee.pattern ? (
                                <p className="text-text-secondary text-xs">{fee.pattern.replace(/_/g, " ")} — {fee.timing.replace(/_/g, " ")} — {fee.payer.replace(/_/g, " ")}</p>
                            ) : feeFile ? (
                                <p className="text-text-secondary text-xs">Receipt uploaded: {feeFile.name}</p>
                            ) : (
                                <p className="text-text-secondary/50 text-xs">Not provided</p>
                            )}
                        </div>

                        {/* Profiles */}
                        <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                            <div className="flex items-center gap-2 mb-2"><Code2 size={15} className="text-purple-soft" /><span className="text-text-primary text-sm font-medium">Connected Profiles</span></div>
                            {Object.keys(profileStatus).length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(profileStatus).map(([p, s]) => (
                                        <span key={p} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                                            s === "oauth" ? "bg-green-pioneer/15 text-green-pioneer" : s === "url" ? "bg-cyan-signal/15 text-cyan-signal" : "bg-white/10 text-text-secondary"
                                        }`}>{p}</span>
                                    ))}
                                </div>
                            ) : <p className="text-text-secondary/50 text-xs">None connected</p>}
                        </div>

                        {/* Documents */}
                        <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                            <div className="flex items-center gap-2 mb-2"><FileText size={15} className="text-amber-builder" /><span className="text-text-primary text-sm font-medium">Documents</span></div>
                            {Object.values(docs).filter(Boolean).length > 0 ? (
                                <p className="text-text-secondary text-xs">{Object.values(docs).filter(Boolean).length} document(s) uploaded</p>
                            ) : <p className="text-text-secondary/50 text-xs">No documents uploaded</p>}
                        </div>

                        {/* Consumption */}
                        {ecomConsent && (
                            <div className="p-4 bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-blue-primary/20 rounded-xl">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp size={15} className="text-blue-primary" />
                                    <span className="text-text-primary text-sm font-medium">Consumption Intelligence</span>
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-primary/15 text-blue-primary">+{ECOM_MOCK_DATA.total_consumption_uplift} pts</span>
                                </div>
                                <p className="text-text-secondary text-xs">Monthly spend ₹{ECOM_MOCK_DATA.monthly_spend_avg.toLocaleString()} · {Object.values(ecomConnections).filter(v => v === "connected").length} platforms connected</p>
                            </div>
                        )}
                    </div>

                    {/* Coverage Meter */}
                    <div className="mb-6">
                        <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Signal Coverage</p>
                        <div className="flex gap-1.5">
                            {[0, 1, 2, 3, 4, 5].map(i => (
                                <div key={i} className={`flex-1 h-2.5 rounded-full transition-colors ${i < coverage ? "bg-blue-primary" : "bg-white/10"}`} />
                            ))}
                        </div>
                        <p className="text-text-secondary text-xs mt-1.5">{coverage} of 6 signal categories covered</p>
                    </div>

                    {/* Consent Summary */}
                    <div className="mb-6">
                        <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-2">Consent Summary</p>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(consents).filter(([, v]) => v).map(([k]) => (
                                <span key={k} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-pioneer/10 text-green-pioneer capitalize">
                                    {k.replace(/_/g, " ")}
                                </span>
                            ))}
                            {ecomConsent && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-pioneer/10 text-green-pioneer capitalize">ecom consumption</span>
                            )}
                        </div>
                    </div>

                    {/* Generate Button */}
                    <div className="bg-gradient-to-r from-purple-500/5 via-indigo-500/5 to-cyan-500/5 p-4 rounded-xl border border-purple-soft/20 mb-4">
                        <div className="flex items-center gap-2 mb-1.5">
                            <Brain size={16} className="text-purple-soft animate-pulse" />
                            <p className="text-text-primary text-sm font-semibold">AI is ready to analyze your profile</p>
                        </div>
                        <p className="text-text-secondary text-xs">Claude Opus will process your data in real-time and generate your intelligent credit score in seconds.</p>
                    </div>
                    
                    <Magnet strength={0.14}>
                        <button onClick={handleGenerateScore}
                            className="w-full sm:w-auto mx-auto generate-glow text-white px-8 py-3 rounded-xl font-semibold text-base hover:shadow-[0_0_40px_rgba(99,102,241,0.5)] transition-all inline-flex items-center justify-center gap-2 active:scale-[0.98]"
                            data-testid="generate-score-btn">
                            <Brain size={18} className="animate-pulse" /> Generate AI Score Now
                        </button>
                    </Magnet>
                </div>
            );

            default: return null;
        }
    };

    return (
        <div className="pt-20 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="onboarding-wizard">
            <div className="max-w-2xl mx-auto">
                <ProgressBar steps={visibleSteps} activeIndex={visibleStepIndex} />

                <AnimatePresence mode="wait" custom={dir}>
                    <motion.div key={step} custom={dir}
                        variants={slideVariants} initial="enter" animate="center" exit="exit"
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                        <GlassCard className="p-6 md:p-8" hover={false} delay={0}>
                            <p className="text-xs uppercase tracking-[0.2em] text-text-secondary/50 mb-4">
                                {STEPS[step]?.label} — Step {visibleStepIndex + 1} of {visibleSteps.length}
                            </p>
                            <ScrollReveal delay={0.02}>
                                {renderStep()}
                            </ScrollReveal>
                        </GlassCard>
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                {step > 0 && step < 8 && (
                    <div className="flex items-center justify-between mt-6">
                        <Magnet strength={0.12}>
                            <button onClick={handleBack}
                                className="text-text-secondary hover:text-text-primary px-4 py-2 rounded-xl transition-all text-sm font-medium inline-flex items-center gap-1 active:scale-[0.98]"
                                data-testid="wizard-back">
                                <ChevronLeft size={16} /> Back
                            </button>
                        </Magnet>
                        <Magnet strength={0.14}>
                            {/* Bug 7: when any ecom account is connected, consent checkbox is required to continue */}
                            {(() => {
                                const ecomConnected = Object.values(ecomConnections).some(v => v === "connected");
                                const ecomConsentRequired = step === 6 && ecomConnected && !ecomConsent;
                                const profileFetching = step === 4 && profileFetchingCount > 0;
                                const isDisabled = profileFetching || ecomConsentRequired;
                                return (
                                <button onClick={handleNext}
                                    disabled={isDisabled}
                                    title={ecomConsentRequired ? "Please check the consent box above to continue" : undefined}
                                    className={`px-6 py-2.5 rounded-xl font-medium text-sm transition-all inline-flex items-center gap-1.5 active:scale-[0.98] ${
                                        isDisabled
                                            ? "bg-white/10 text-text-secondary cursor-not-allowed"
                                            : "bg-blue-primary text-white hover:bg-blue-primary/90"
                                    }`}
                                    data-testid="wizard-next">
                                    {profileFetching ? (
                                        <><Loader2 size={14} className="animate-spin" /> Fetching {profileFetchingCount} profile{profileFetchingCount > 1 ? "s" : ""}...</>
                                    ) : ecomConsentRequired ? (
                                        <>Consent required <ChevronRight size={16} /></>
                                    ) : (
                                        <>{step === 7 ? "Review" : step === 6 && !ecomConsent ? "Skip" : "Continue"} <ChevronRight size={16} /></>
                                    )}
                                </button>
                                );
                            })()}
                        </Magnet>
                    </div>
                )}
                {step === 8 && (
                    <div className="mt-6">
                        <Magnet strength={0.12}>
                            <button onClick={handleBack}
                                className="text-text-secondary hover:text-text-primary px-4 py-2 rounded-xl transition-all text-sm font-medium inline-flex items-center gap-1 active:scale-[0.98]"
                                data-testid="wizard-back">
                                <ChevronLeft size={16} /> Back
                            </button>
                        </Magnet>
                    </div>
                )}
            </div>

            <DigiLockerModal
                open={digilockerModalOpen}
                onClose={() => setDigilockerModalOpen(false)}
                onUseUpload={() => setAcademicTab("upload")}
                onUseManual={() => setAcademicTab("manual")}
            />

            <AnimatePresence>
                {generating && (
                    <ScoreLoadingOverlay
                        error={genError}
                        onComplete={() => {}}
                        onRetry={() => { setGenError(null); setGenerating(true); handleGenerateScore(); }}
                        onGoBack={() => { setGenerating(false); setGenError(null); }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
